
import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SnackbarAction, SnackbarKey, useSnackbar } from 'notistack';

import { useTheme } from '@mui/material/styles';

import { Controller, FieldArray, FieldArrayMethodProps, FieldPath, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import MoreIcon from '@mui/icons-material/More';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import ClearIcon from '@mui/icons-material/Clear';

import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

import { ThreeDots, Watch } from "react-loader-spinner";


import useEntityService, { useBasicFilterEntity } from 'features/services/Entity';
import {
  currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom,
  isSearchBoxShowAtom, isSaveLoadingAtom
} from 'library/store';
import useRegistrationService, { useBasicFilterRegistration } from './services/Registration';


import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Slide, Typography, Chip, FormHelperText, Link, ToggleButtonGroup, ToggleButton } from '@mui/material';
import NumberFormat from 'react-number-format';

import { IRegistration, IRegistrationMaterial, defaultRegistration } from './models/Registration';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';


import IEnumeration, {
  IEnumerationItem, Enum_REGISTRATION_STATUS, Enum_STUDY_TYPE, Enum_SCHOOL_MATERIAL, Enum_SCHOOL_MATERIAL_STATUS,
  Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_ARTICLE_OPTION, Enum_STUDENT_FROM,
  Enum_STUDY_SECTION, Enum_REGISTRATION_WITHDRAWAL, Enum_REGISTRATION_EXPEL, Enum_SOCIAL_CASE_REASON, Enum_CHRONIC_ILLNESS,
  Enum_DISABILITY_TYPE, Enum_SPECIFIC_NEED_PURPOSE
} from 'features/configuration/models/Enumeration';
import { ITarification } from 'features/configuration/models/Tarification';


import { IExtensionType } from 'features/configuration/models/ExtensionType';


import { justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';

import useSchoolYearService, { useBasicFilterSchoolYear } from 'features/setup/services/SchoolYear';
import { ISchoolYear, ISchoolYearClass, ISchoolYearClassTemplate } from 'features/setup/models/SchoolYear';

import useBusDestinationService, { useBasicFilterBusDestination } from 'features/setup/services/BusDestination';
import { IBusDestination } from 'features/setup/models/BusDestination';

import { DatePicker } from '@mui/x-date-pickers';
import { IClassTemplate } from 'features/setup/models/ClassTemplate';
import useClassTemplateService, { useBasicFilterClassTemplate } from 'features/setup/services/ClassTemplate';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { IBilling, defaultBilling } from 'features/finance/models/Billing';
import { IPerson } from './models/Person';
import usePersonService, { useBasicFilterPerson, useBasicFilterPersonSearch } from './services/Person';
import { IBillingType } from 'features/setup/models/BillingType';
import useBillingTypeService, { useBasicFilterBillingType, useBasicFilterArticleBillingType, useBasicFilterTransportBillingType } from 'features/setup/services/BillingType';
import ExtensionValueField from 'components/ui/ExtensionValueField';
import { debounce } from 'lodash';
import { GrClose, GrSearch, GrSearchAdvanced } from 'react-icons/gr';
import WidgetSummaryBasic from 'features/widgets/WidgetSummaryBasic';

import { Colors } from 'themes/theme';
import EnhancedTable from 'components/ui/EnhancedTable';
import { ITellerOperation } from 'features/finance/models/TellerOperation';
import { Print } from '@mui/icons-material';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';


export const RegistrationForm: FC<IRegistration> = (props: IRegistration = { ...defaultRegistration }) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { id } = useParams();

  const theme = useTheme();

  const { language: lg, schoolYears, applicationSetup, roleEntities } = useRecoilValue(currentUserSessionAtom);

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer, openEntityPrintDrawer,
    checkEntityExpressionSyntax, checkEntitySaveAuthorization } = useEntityService();

  const { getEnumerationItemsByEnumerationCodes, getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();

  const { createRegistration, updateRegistration, getRegistrationsByStudent,
    generateIntialRegistration, evaluateAddedBillings,
    createNewBillings, createNewArticleBillings, createNewTransportBillings, changeClass, resetClass } = useRegistrationService();

  //const { getSchoolYearClasses, getSchoolYearClassTemplates } = useSchoolYearService();
  const schoolYearServiceRef = useRef(useSchoolYearService());

  const { getPersons, getPersonsSearchCount, getParentPortables, getParentEmails } = usePersonService();

  const { getAvailableArticleBillingTypes } = useBillingTypeService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);
  //const refSchoolYears = useRef<ISchoolYear[]>();

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterRegistration = useBasicFilterRegistration(
    (event: React.MouseEvent<unknown>, row: IRegistration) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const { canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();


  async function selectDefaultClass(personId: number) {

    if (personId <= 0) return;

    const arrOtherRegistrations = await getRegistrationsByStudent(personId);

    const schoolYear = schoolYears.find(x => x.id === watchSchoolYearId);

    if (!(arrOtherRegistrations && arrOtherRegistrations.length > 0 && schoolYear))
      return;

    if (getValues().id <= 0 && arrOtherRegistrations.some(x => ['10', '95'].includes(x.status) && x.schoolYearId === watchSchoolYearId)) {
      enqueueSnackbar(t('There is already a registration for this student this year'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 3000
      });
      return;
    }

    const prevRegistration = arrOtherRegistrations.find(x => x.schoolYearYear + 1 === schoolYear.year && x.status === '10' &&
      x.schoolYearCategory === schoolYear.category && (x.succeededInHigherClass || x.defaultSuccessMark <= x.annualAverageMark));

    if (isFalsy(prevRegistration)) return;

    const schoolYearClassTemplate = schoolYearClassTemplates.find(x => x.classTemplateLevel === prevRegistration.classTemplateLevel + 1);

    if (isFalsy(schoolYearClassTemplate)) return;

    setValue('studySection', prevRegistration.studySection);
    setValue('studyLanguage', prevRegistration.studyLanguage);
    setValue('schoolYearClassTemplateId', schoolYearClassTemplate.id);
  }

  const [openPersonFilter, setOpenPersonFilter] = useState(false);
  const basicFilterPerson = useBasicFilterPerson(
    async (event: React.MouseEvent<unknown>, row: IPerson) => {
      const { id, firstName, lastName, birthDate, birthPlace, particularSign, gender, civility,
          fatherId, motherId, tutorId,
          birthCertificateNumber, civilRegistrar, birthCertificateDelivrancePlace, studentCode } = row;

      //setValue(`${currentParentType}Id` as FieldPath<IRegistration>, 0);
      //setValue(`${currentParentType}FirstName` as FieldPath<IRegistration>, '');
      setOpenPersonFilter(false);

      if(refPersonType.current === 'student') {
        setValue('personId', id);
        setValue('firstName', firstName);
        setValue('lastName', lastName);
        setValue('birthDate', birthDate);
        setValue('birthPlace', birthPlace);
        setValue('particularSign', particularSign);
        setValue('gender', gender);
        setValue('civility', civility);

        setValue('birthCertificateNumber', birthCertificateNumber);
        setValue('civilRegistrar', civilRegistrar);
        setValue('birthCertificateDelivrancePlace', birthCertificateDelivrancePlace);

        setValue('studentCode', studentCode);

        // We reset this to empty because we want use Effect to load it. 
        // by checking watchFatherId, watchMotherId, watchTutorId
        setValue('fatherFirstName', '');
        setValue('fatherLastName', '');
        setValue('fatherBirthPlace', '');
        setValue('motherFirstName', '');
        setValue('motherLastName', '');
        setValue('motherBirthPlace', '');
        setValue('tutorFirstName', '');
        setValue('tutorLastName', '');
        setValue('tutorBirthPlace', '');

        setValue('fatherId', fatherId);
        setValue('motherId', motherId);
        setValue('tutorId', tutorId);

        if (getValues().status === '95')
          await selectDefaultClass(id);
      } else {
        setValue(`${currentParentType}Id` as FieldPath<IRegistration>, id);
        setValue(`${currentParentType}FirstName` as FieldPath<IRegistration>, firstName);
        setValue(`${currentParentType}LastName` as FieldPath<IRegistration>, lastName);
        setValue(`${currentParentType}BirthPlace` as FieldPath<IRegistration>, birthPlace);
        setValue(`${currentParentType}ParticularSign` as FieldPath<IRegistration>, particularSign);
        setValue(`${currentParentType}Civility` as FieldPath<IRegistration>, civility);
        setValue(`${currentParentType}Gender` as FieldPath<IRegistration>, gender);
        setValue(`${currentParentType}BirthCertificateNumber` as FieldPath<IRegistration>, birthCertificateNumber);
        setValue(`${currentParentType}CivilRegistrar` as FieldPath<IRegistration>, civilRegistrar);
        setValue(`${currentParentType}BirthCertificateDelivrancePlace` as FieldPath<IRegistration>, birthCertificateDelivrancePlace);     
      }

      
    
    }
  );

  const [openBusDestinationFilter, setOpenBusDestinationFilter] = useState(false);
  const basicFilterBusDestination = useBasicFilterBusDestination(
    (event: React.MouseEvent<unknown>, row: IBusDestination) => {
      const { id, name, isActive } = row;

      if(!isActive) {
        enqueueSnackbar(t('This destination is not active'), {
          variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        return;
      }
      setValue('busDestinationId', id);
      setValue('busDestinationName', name);

      setOpenBusDestinationFilter(false);
    }
  );


  const [selectedFilterBillingTypes, setSelectedFilterBillingTypes] = useState<number[]>([]);
  const [filteredBillingTypes, setFilteredBillingTypes] = useState<IBillingType[]>([]);

  const [openBillingTypeFilter, setOpenBillingTypeFilter] = useState(false);
  const basicFilterBillingType = useBasicFilterBillingType(
    async (event: React.MouseEvent<unknown>, row: IBillingType) => {
      const { name, description, id, isArticle, isTransport } = row;


      if (isArticle) {
        enqueueSnackbar(t('Articles can not be added here'), {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        return;
      }

      if (isTransport) {
        enqueueSnackbar(t('Transport can not be added here'), {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        return;
      }

      if (currentBillingUsage === 'billing')
        (refAppendBillings.current ?? emptyFunc)(
          {
            id: 0, registrationId: _id, billingTypeId: id,
            isObligatory: false, isCancelled: false, cancellationPurpose: '', cancellationDate: null,
            billingTypeName: name,
            amount: 0, amountPaid: 0, unpaidAmount: 0, dueAmount: 0
          });
      else {
        // setFilteredBillingTypes([{...row}]);
        // setSelectedFilterBillingTypes([id]);
        // await handleOkBasicTextFilterBillingTypeForm();
        await evaluateNewBillingsAdded([{ ...row }], [id]);
      }
      // (refAppendNewBillings.current??emptyFunc)(
      //   {id:0, registrationId: _id, billingTypeId: id, 
      //     isObligatory: false, isCancelled: false, cancellationPurpose: '', cancellationDate: null,
      //     billingTypeName: name, articleOption: '', articleQuantity: 0, 
      //     amount: 0, amountPaid : 0, unpaidAmount: 0, dueAmount: 0 });

      //   setOpenBillingTypeFilter(false);
    }, {
    rowCheckedMode: 'multiple',
    stateSelected: [selectedFilterBillingTypes, setSelectedFilterBillingTypes],
    stateFiltered: [filteredBillingTypes, setFilteredBillingTypes],
  }
  );

  const [selectedFilterArticleBillingTypes, setSelectedFilterArticleBillingTypes] = useState<number[]>([]);
  const [filteredArticleBillingTypes, setFilteredArticleBillingTypes] = useState<IBillingType[]>([]);

  const [openArticleBillingTypeFilter, setOpenArticleBillingTypeFilter] = useState(false);
  const basicFilterArticleBillingType = useBasicFilterArticleBillingType(
    async (event: React.MouseEvent<unknown>, row: IBillingType) => {
      const { name, description, id, deliveryArticleId } = row;


      if (currentBillingUsage === 'billing') {
        (refAppendBillings.current ?? emptyFunc)(
          {
            id: 0, registrationId: _id, billingTypeId: id,
            isObligatory: false, isCancelled: false, cancellationPurpose: '', cancellationDate: null,
            billingTypeName: name,
            amount: 0, amountPaid: 0, unpaidAmount: 0, dueAmount: 0
          });
        setOpenArticleBillingTypeFilter(false);

      } else {
        // setFilteredArticleBillingTypes([{...row}]);
        // setSelectedFilterArticleBillingTypes([deliveryArticleId]);

        // await handleOkBasicTextFilterArticleBillingTypeForm();
        await evaluateNewArticleBillingsAdded([{ ...row }], [deliveryArticleId]);
      }
      // (refAppendNewBillings.current??emptyFunc)(
      //   {id:0, registrationId: _id, billingTypeId: id, 
      //     isObligatory: false, isCancelled: false, cancellationPurpose: '', cancellationDate: null,
      //     billingTypeName: name, articleOption: '', articleQuantity: 0, 
      //     amount: 0, amountPaid : 0, unpaidAmount: 0, dueAmount: 0 });


    }, {
    rowCheckedMode: 'multiple',
    stateSelected: [selectedFilterArticleBillingTypes, setSelectedFilterArticleBillingTypes],
    stateFiltered: [filteredArticleBillingTypes, setFilteredArticleBillingTypes],
  }
  );

  const [selectedFilterTransportBillingTypes, setSelectedFilterTransportBillingTypes] = useState<number[]>([]);
  const [filteredTransportBillingTypes, setFilteredTransportBillingTypes] = useState<IBillingType[]>([]);

  const [openTransportBillingTypeFilter, setOpenTransportBillingTypeFilter] = useState(false);
  const basicFilterTransportBillingType = useBasicFilterTransportBillingType(
    async (event: React.MouseEvent<unknown>, row: IBillingType) => {
      const { name, description, id } = row;

      if (currentBillingUsage === 'billing') {
        (refAppendBillings.current ?? emptyFunc)(
          {
            id: 0, registrationId: _id, billingTypeId: id,
            isObligatory: false, isCancelled: false, cancellationPurpose: '', cancellationDate: null,
            billingTypeName: name,
            amount: 0, amountPaid: 0, unpaidAmount: 0, dueAmount: 0
          });
        setOpenTransportBillingTypeFilter(false);
      } else {

        // setFilteredTransportBillingTypes([{...row}]);
        // setSelectedFilterTransportBillingTypes([id]);

        // await handleOkBasicTextFilterTransportBillingTypeForm();
        await evaluateNewTransportBillingsAdded([{ ...row }], [id]);
      }


    }, {
    rowCheckedMode: 'multiple',
    stateSelected: [selectedFilterTransportBillingTypes, setSelectedFilterTransportBillingTypes],
    stateFiltered: [filteredTransportBillingTypes, setFilteredTransportBillingTypes],
  }
  );

  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity(
    (event: React.MouseEvent<unknown>, row: IEntity) => {
      const { name, description } = row;

      // setValue('entityName', name || '');
      // setValue('entityDescription', description || '');

      setOpenEntityFilter(false);
    }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration(
    (event: React.MouseEvent<unknown>, row: IEnumeration) => {
      const { id, name, description } = row;

      // setValue('enumerationId', id);
      // setValue('enumerationName', name);

      setOpenEnumerationFilter(false);
    }
  );

  const emptyFunc = (obj: any) => { }

  const methods = useForm<IRegistration>({
    defaultValues: {
      ...defaultRegistration,
      schoolYearId: applicationSetup.currentSchoolYearId
    }
  });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;


  const watchSchoolYearId = watch('schoolYearId');
  const watchStudyType = watch('studyType');
  const watchStudySection = watch('studySection');
  const watchStudyLanguage = watch('studyLanguage');
  const watchStudentFrom = watch('studentFrom');

  const watchSocialCaseReason = watch('socialCaseReason');
  const watchIsSocialCase = watch('isSocialCase');

 
  const watchSchoolYearClassTemplateId = watch('schoolYearClassTemplateId');

  const watchSchoolYearClassId = watch('schoolYearClassId');

  const watchGender = watch('gender');

  const watchFatherGender = watch('fatherGender');
  const watchMotherGender = watch('motherGender');
  const watchTutorGender = watch('tutorGender');

  const watchFirstName = watch('firstName');
  const watchLastName = watch('lastName');
  const watchParticularSign = watch('particularSign');
  const watchPersonId = watch('personId');

  const watchChronicIllnessCode = watch('chronicIllnessCode');
  const watchSpecificNeedPurposeCode = watch('specificNeedPurposeCode');
  const watchDisabilityType = watch('disabilityType');

  const watchFatherId = watch('fatherId');
  const watchMotherId = watch('motherId');
  const watchTutorId = watch('tutorId');

  const watchHasSpecificNeeds = watch('hasSpecificNeeds');
  const watchHasDisability = watch('hasDisability');

  // let { fields, append: appendCoverages, update: updateCoverage ,remove: removeCoverage,  } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `productCoverages`,
  //   control,            
  // });


  const [openPersonSearchFilter, setOpenPersonSearchFilter] = useState(false);
  const basicFilterPersonSearch = useBasicFilterPersonSearch(
    () => {
      const { firstName, lastName, particularSign } = getValues();
      return { firstName, lastName, particularSign };
      //return refPersonSearch.current || {firstName: '', lastName: '', particularSign: ''};
    },
    async (event: React.MouseEvent<unknown>, row: IPerson) => {
      const { id, firstName, lastName, birthDate, birthPlace, particularSign, civility, gender, 
        fatherId, motherId, tutorId,
        birthCertificateNumber, civilRegistrar, birthCertificateDelivrancePlace, studentCode } = row;

      if(refPersonType.current === 'student') {
        setValue('personId', id);
        setValue('firstName', firstName);
        setValue('lastName', lastName);
        setValue('birthDate', birthDate);
        setValue('birthPlace', birthPlace);
        setValue('particularSign', particularSign);
        setValue('gender', gender);
        setValue('civility', civility);

        setValue('birthCertificateNumber', birthCertificateNumber);
        setValue('civilRegistrar', civilRegistrar);
        setValue('birthCertificateDelivrancePlace', birthCertificateDelivrancePlace);

        setValue('studentCode', studentCode);

        // We reset this to empty because we want use Effect to load it. 
        // by checking watchFatherId, watchMotherId, watchTutorId
        setValue('fatherFirstName', '');
        setValue('fatherLastName', '');
        setValue('fatherBirthPlace', '');
        setValue('motherFirstName', '');
        setValue('motherLastName', '');
        setValue('motherBirthPlace', '');
        setValue('tutorFirstName', '');
        setValue('tutorLastName', '');
        setValue('tutorBirthPlace', '');

        setValue('fatherId', fatherId);
        setValue('motherId', motherId);
        setValue('tutorId', tutorId);


        if (getValues().status === '95')
          await selectDefaultClass(id);

      } else {
        setValue(`${currentParentType}Id` as FieldPath<IRegistration>, id);
        setValue(`${currentParentType}FirstName` as FieldPath<IRegistration>, firstName);
        setValue(`${currentParentType}LastName` as FieldPath<IRegistration>, lastName);
        setValue(`${currentParentType}BirthPlace` as FieldPath<IRegistration>, birthPlace);
        setValue(`${currentParentType}ParticularSign` as FieldPath<IRegistration>, particularSign);
        setValue(`${currentParentType}Civility` as FieldPath<IRegistration>, civility);
        setValue(`${currentParentType}Gender` as FieldPath<IRegistration>, gender);
        setValue(`${currentParentType}BirthCertificateNumber` as FieldPath<IRegistration>, birthCertificateNumber);
        setValue(`${currentParentType}CivilRegistrar` as FieldPath<IRegistration>, civilRegistrar);
        setValue(`${currentParentType}BirthCertificateDelivrancePlace` as FieldPath<IRegistration>, birthCertificateDelivrancePlace);     
      }

      setOpenPersonSearchFilter(false);
    }
  );

  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<IRegistration>, Error, IRegistration>(
    _id > 0 ? updateRegistration : createRegistration, {
    onSuccess: (data: IResult<IRegistration>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      
      _setId(data.data.id);

      // we set id for entity that can be created during save of registration
      setValue('fatherId', data.data.fatherId);
      setValue('motherId', data.data.motherId);
      setValue('tutorId', data.data.tutorId);

      setOpenNewBilling(false);
      //setCurrentEntityIdForAction(data.data.id);
      setCurrentFormNameAtom(`${t('Registration')} - # ${data.data.id} # - ${data.data.isRepeater ? `(${t('Repeater')})` : ''}`);
      queryClient.invalidateQueries(['Registration', data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
      setOpenNewBilling(false);
    }
  });

  //// Mutation for actions.....
  const { isLoading: isLoadingNewArticle, isError: isErrorNewArticle, isSuccess: isSuccessNewArticle, error: errorNewArticle,
    mutate: mutateNewArticles } = useMutation<IResult<IRegistration>, Error, { id: number, billings: IBilling[] }>(
      createNewArticleBillings, {
      onSuccess: (data: IResult<IRegistration>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);

        reset(data.data);
        setOpenNewArticleBilling(false);
        queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {

        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenNewArticleBilling(false);
      }
    });

  const { isLoading: isLoadingNewTransport, isError: isErrorNewTransport, isSuccess: isSuccessNewTransport, error: errorNewTransport,
    mutate: mutateNewTransports } = useMutation<IResult<IRegistration>, Error, { id: number, billings: IBilling[] }>(
      createNewTransportBillings, {
      onSuccess: (data: IResult<IRegistration>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);
        setIsSaveLoading(false);
        reset(data.data);
        setOpenNewTransportBilling(false);
        queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {
        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenNewTransportBilling(false);
        setIsSaveLoading(false);
      }
    });

  const { isLoading: isLoadingNewBilling, isError: isErrorNewBilling, isSuccess: isSuccessNewBilling, error: errorNewBilling,
    mutate: mutateNewBillings } = useMutation<IResult<IRegistration>, Error, { id: number, billings: IBilling[] }>(
      createNewBillings, {
      onSuccess: (data: IResult<IRegistration>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);
        setIsSaveLoading(false);
        reset(data.data);
        setOpenNewTransportBilling(false);
        queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {
        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenNewTransportBilling(false);
        setIsSaveLoading(false);
      }
    });


  const { isLoading: isLoadingChangeClass, isError: isErrorChangeClass, isSuccess: isSuccessChangeClass, error: errorChangeClass,
    mutate: mutateChangeClass } = useMutation<IResult<IRegistration>, Error, { id: number, newClassId: number }>(
      changeClass, {
      onSuccess: (data: IResult<IRegistration>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);
        setIsSaveLoading(false);
        // reset(data.data);
        setValue('schoolYearClassId', data.data.schoolYearClassId);
        setOpenChangeClass(false);
        queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {
        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenChangeClass(false);
        setIsSaveLoading(false);
      }
    });

  const { isLoading: isLoadingResetClass, isError: isErrorResetClass, isSuccess: isSuccessResetClass, error: errorResetClass,
    mutate: mutateResetClass } = useMutation<IResult<IRegistration>, Error, { id: number, newClassId: number }>(
      resetClass, {
      onSuccess: (data: IResult<IRegistration>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);
        setIsSaveLoading(false);
        //reset(data.data);
        setValue('schoolYearClassId', data.data.schoolYearClassId);
        setOpenResetClass(false);
        queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {
        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenResetClass(false);
        setIsSaveLoading(false);
      }
    });

  const { data: _data, refetch } = useQuery<IRegistration>(['Registration', _id], () => retrieveEntity('Registration', _id, 'Basic'),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: extensionEnumerationItems } = useQuery<IEnumerationItem[]>(['SchoolYear', 'EnumerationItem', watchSchoolYearId], () =>
    getExtensionEnumerationItemsBySchoolYear(watchSchoolYearId));

  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'Registration'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_STUDY_LEVEL, Enum_STUDY_SECTION, Enum_STUDY_LANGUAGE, Enum_REGISTRATION_STATUS, Enum_ARTICLE_OPTION, Enum_STUDENT_FROM,
      Enum_STUDY_TYPE, Enum_SCHOOL_MATERIAL, Enum_SCHOOL_MATERIAL_STATUS,
      Enum_SOCIAL_CASE_REASON, Enum_CHRONIC_ILLNESS, Enum_SPECIFIC_NEED_PURPOSE, Enum_DISABILITY_TYPE]));

  const { data: enumItems2 } = useQuery<IEnumerationItem[]>(['EnumerationItems2', 'Registration'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_REGISTRATION_WITHDRAWAL, Enum_REGISTRATION_EXPEL, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY]));


  //const {data: schoolYears} = useQuery<ISchoolYear[]>( ['SchoolYears'], () => getSchoolYears( {year: 0, name: ''}) );

  const [openMorePersonInfo, setOpenMorePersonInfo] = useState(false);

  type ParentType = 'father' | 'mother' | 'tutor';
  const [currentParentType, setCurrentParentType] = useState<ParentType>('father');
  const refParentType = useRef<ParentType>('father');
  const handleChangeCurrentParentType = (event: React.MouseEvent<HTMLElement>, newParentType: ParentType) => {
    if (isFalsy(newParentType))
      return;

    
    setCurrentParentType(newParentType);
    refParentType.current = newParentType;
  }

  const [schoolYearClassTemplates, setSchoolYearClassTemplates] = useState<ISchoolYearClassTemplate[]>([]);
  const [classes, setClasses] = useState<ISchoolYearClass[]>([]);

  const [openBillingExtension, setOpenBillingExtension] = useState<boolean>(false);

  const [openArticleAvailable, setOpenArticleAvailable] = useState<boolean>(false);
  const [openBillingOption, setOpenBillingOption] = useState<boolean>(false);
  const getInputAdornmentBillingOption = (row: IBilling, cellId: keyof IBilling) => ({

    toolTip: 'Details',
    icon: MoreIcon,
    iconDisable: !row.isArticle,
    onClickIcon: (event: any, index: number, row: IBilling) => {

      setBillingIndex(index);
      setCurrentBilling(row);

      setOpenBillingOption(true);
    }
  })

  const [headBillingCells, setHeadBillingCells] = useState<HeadCell<IBilling>[]>([]);
  useEffect(() => {
    setHeadBillingCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },
      {
        id: 'billingTypeName', label: t('Billing'), display: true, type: 'string', width: 40,
        getInputAdornment: getInputAdornmentBillingOption
      },
      { id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 15 },
      { id: 'amountPaid', label: t('Amount paid'), display: true, type: 'numeric', width: 15 },
      { id: 'unpaidAmount', label: t('Unpaid amount'), display: true, type: 'numeric', width: 15 },
      { id: 'dueAmount', label: t('Due amount'), display: true, type: 'numeric', width: 15 },
    ])
  }, [t, i18n])

  const refAppendBillings = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateBilling = useRef<(index: number, value: Partial<FieldArray<IBilling>>) => void>(null);
  const refRemoveBilling = useRef<(index: number) => void>(null);

  const [currentBilling, setCurrentBilling] = useState<IBilling>(defaultBilling);
  const [billingIndex, setBillingIndex] = useState<number>(-1);
  const billingRowActionIcon = (billing: IBilling): ActionIconTableRow<IRegistration, IBilling> => {

    const res: ActionIconTableRow<IRegistration, IBilling> = {
      toolTip: 'Remove',
      icon: SystemUpdateAltIcon,
      hasAction: (index: number, row: IBilling) => !row.isCancelled && row.isArticle, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IBilling) => {

        const billing = getValues().billings.at(index);
        if (isFalsy(billing)) return;

        openEntityActionDrawer('Billing', billing.id);
      }
    }
    return res;
  }

  const handleBillingDoubleClick = (event: React.MouseEvent<unknown>, index: number, row: IBilling) => {
    const roleEntityBilling = roleEntities.find(re => re.entityName === 'Billing');
    if (isFalsy(roleEntityBilling)) return;

    const billing = getValues().billings.at(index);
    if (isFalsy(billing)) return;

    navigate(`/billing/${billing.id}`);
  }

  const [optionAvailableArticles, setOptionAvailableArticles] = useState<IBillingType[]>([]);
  const handleArticleAvailableOption = async (event: any) => {

    const { billingTypeName, billingTypeId } = currentBilling;
    const arr = await getAvailableArticleBillingTypes({ code: '', name: billingTypeName, description: '' });
    const arrAvailableArticles = arr.filter(x => x.id === billingTypeId);
    setOptionAvailableArticles(arrAvailableArticles);
    setOpenArticleAvailable(true);
  }

  const handleOptionAvailableArticleDoubleClick = (event: React.MouseEvent<unknown>, row: IBillingType) => {

    const billing = getValues().billings.at(billingIndex);
    if (billingIndex < 0 || isFalsy(billing)) return;

    const { deliveryArticleId, articleOption, articleOptionName, deliveryDescription } = row;
    (refUpdateBilling.current ?? emptyFunc)(billingIndex, {
      ...billing!,
      deliveryArticleId, articleOption, articleOptionName, deliveryDescription
    });

    setOpenArticleAvailable(false);
  }


  const getArticleOptionList = (row: IBilling, cellId: keyof IBilling,
    opts: { value: string | number, name: string }[]) => {

    return getAsOptions(refEnumItems.current ?? [], Enum_ARTICLE_OPTION);
  }

  const cellEditableNewBilling = (row: IBilling, cellId: keyof IBilling) => {
    return true;
  }

  const [headNewBillingCells, setHeadNewBillingCells] = useState<HeadCell<IBilling>[]>([]);
  useEffect(() => {
    setHeadNewBillingCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },
      { id: 'billingTypeName', label: t('Billing'), display: true, type: 'string', width: 75, },
      // {id:'articleOption', label : t('Option'),  display: true, type: 'string', width: 35, 
      //   isEditable: cellEditableNewBilling, getOptions: getArticleOptionList},
      // {id:'articleQuantity', label : t('Quantity'),  display: true, type: 'numeric', width: 15,
      //   isEditable: cellEditableNewBilling},
      { id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 25 },

    ])
  }, [t, i18n])

  const refAppendNewBillings = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateNewBilling = useRef<(index: number, value: Partial<FieldArray<IBilling>>) => void>(null);
  const refRemoveNewBilling = useRef<(index: number) => void>(null);

  const [newBillingIndex, setNewBillingIndex] = useState<number>(-1);
  const newBillingRowActionIcon = (billing: IBilling): ActionIconTableRow<IRegistration, IBilling> => {

    const res: ActionIconTableRow<IRegistration, IBilling> = {
      toolTip: 'Remove',
      icon: RemoveCircleIcon,
      hasAction: (index: number, row: IBilling) => true, //!row.isObligatory, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IBilling) => {

        (refRemoveNewBilling.current ?? emptyFunc)(index);
      }
    }
    return res;
  }


  const [headNewArticleBillingCells, setHeadNewArticleBillingCells] = useState<HeadCell<IBilling>[]>([]);
  useEffect(() => {
    setHeadNewArticleBillingCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },
      { id: 'billingTypeName', label: t('Billing'), display: true, type: 'string', width: 45, },
      { id: 'articleOption', label: t('Option'), display: true, type: 'string', width: 35, getOptions: getArticleOptionList },
      // {id:'articleQuantity', label : t('Quantity'),  display: true, type: 'numeric', width: 15,
      //   isEditable: cellEditableNewBilling},
      { id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 20 },

    ])
  }, [t, i18n])

  const refAppendNewArticleBillings = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateNewArticleBilling = useRef<(index: number, value: Partial<FieldArray<IBilling>>) => void>(null);
  const refRemoveNewArticleBilling = useRef<(index: number) => void>(null);

  const newArticleBillingRowActionIcon = (billing: IBilling): ActionIconTableRow<IRegistration, IBilling> => {

    const res: ActionIconTableRow<IRegistration, IBilling> = {
      toolTip: 'Remove',
      icon: RemoveCircleIcon,
      hasAction: (index: number, row: IBilling) => !row.isObligatory, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IBilling) => {

        (refRemoveNewArticleBilling.current ?? emptyFunc)(index);
      }
    }
    return res;
  }


  const [headNewTransportBillingCells, setHeadNewTransportBillingCells] = useState<HeadCell<IBilling>[]>([]);
  useEffect(() => {
    setHeadNewTransportBillingCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },
      { id: 'billingTypeName', label: t('Billing'), display: true, type: 'string', width: 50, },
      { id: 'isArrival', label: t('Arrival ?'), display: true, type: 'boolean', width: 15, },
      { id: 'isDeparture', label: t('Departure ?'), display: true, type: 'boolean', width: 15, },

      //{id:'articleOption', label : t('Option'),  display: true, type: 'string', width: 35, getOptions: getArticleOptionList},
      // {id:'articleQuantity', label : t('Quantity'),  display: true, type: 'numeric', width: 15,
      //   isEditable: cellEditableNewBilling},
      { id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 20 },

    ])
  }, [t, i18n])

  const refAppendNewTransportBillings = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateNewTransportBilling = useRef<(index: number, value: Partial<FieldArray<IBilling>>) => void>(null);
  const refRemoveNewTransportBilling = useRef<(index: number) => void>(null);

  const newTransportBillingRowActionIcon = (billing: IBilling): ActionIconTableRow<IRegistration, IBilling> => {

    const res: ActionIconTableRow<IRegistration, IBilling> = {
      toolTip: 'Remove',
      icon: RemoveCircleIcon,
      hasAction: (index: number, row: IBilling) => !row.isObligatory, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IBilling) => {

        (refRemoveNewTransportBilling.current ?? emptyFunc)(index);
      }
    }
    return res;
  }

  const getMaterialList = (row: IRegistrationMaterial, cellId: keyof IRegistrationMaterial,
    opts: { value: string | number, name: string }[]) => {

    return getAsOptions(refEnumItems.current ?? [], Enum_SCHOOL_MATERIAL);
  }

  const getMaterialStatusList = (row: IRegistrationMaterial, cellId: keyof IRegistrationMaterial,
    opts: { value: string | number, name: string }[]) => {

    return getAsOptions(refEnumItems.current ?? [], Enum_SCHOOL_MATERIAL_STATUS);
  }

  const [headRegistrationMaterialCells, setHeadRegistrationMaterialCells] = useState<HeadCell<IRegistrationMaterial>[]>([]);
  useEffect(() => {
    setHeadRegistrationMaterialCells([
      { id: 'id', label: t('Id'), display: false, type: 'numeric', },
      {
        id: 'schoolMaterialCode', label: t('Name'), display: true, type: 'string', width: 45,
        getOptions: getMaterialList
      },

      // {id:'isGivenByStudent', label : `${t('Given by student ?')}`,  display: true, type: 'boolean', width: 15, 
      //   isEditable: cellEditableMaterial },
      { id: 'status', label: `${t('Status')}`, display: true, type: 'string', width: 35, getOptions: getMaterialStatusList },
      { id: 'statusDate', label: `${t('Date')}`, display: true, type: 'date', width: 20, },
    ])
  }, [t, i18n])

  const refAppendRegistrationMaterials = useRef<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateRegistrationMaterial = useRef<(index: number, value: Partial<FieldArray<ISchoolYear>>) => void>(null);
  const refRemoveRegistrationMaterial = useRef<(index: number) => void>(null);


  const [registrationMaterialIndex, setRegistrationMaterialIndex] = useState<number>(-1);
  const registrationMaterialRowActionIcon = (registrationMaterial: IRegistrationMaterial): ActionIconTableRow<IRegistration, IRegistrationMaterial> => {

    const res: ActionIconTableRow<IRegistration, IRegistrationMaterial> = {
      toolTip: 'remove',
      icon: registrationMaterial.status === '95' ? DoneOutlineIcon : ClearIcon,
      hasAction: (index: number, row: IRegistrationMaterial) => {
        const roleEntityRegistrationMaterial = roleEntities.find(re => re.entityName === 'RegistrationMaterial');
        return !(isFalsy(roleEntityRegistrationMaterial));
      }, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IRegistrationMaterial) => {

        const regMaterial = getValues().registrationMaterials.at(index);
        if (isFalsy(regMaterial)) return;

        openEntityActionDrawer('RegistrationMaterial', regMaterial.id);
        //(refRemoveRegistrationMaterial.current??emptyFunc)(index);            
      }
    }
    return res;
  }


  const tellerOperationRowActionIcon = (tellerOperation: ITellerOperation): ActionIconTableRow<IRegistration, ITellerOperation> => {

    const res: ActionIconTableRow<IRegistration, ITellerOperation> = {
      toolTip: t('Print'),
      icon: Print,
      hasAction: (index: number, row: ITellerOperation) => {
        const roleEntityTellerOperation = roleEntities.find(re => re.entityName === 'TellerOperation');
        return roleEntityTellerOperation?.printAllowed ?? false;
      }, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: ITellerOperation) => {
        openEntityPrintDrawer('TellerOperation', row.id);
        //(refRemoveBilling.current??emptyFunc)(index);            
      }
    }
    return res;
  }

  const handleGoToTellerOperation = (event: any) => {
    const roleEntityTellerOperation = roleEntities.find(re => re.entityName === 'TellerOperation');
    if (isFalsy(roleEntityTellerOperation)) return;

    navigate(`/tellerOperation/newOperation?registrationId=${getValues().id}`);
  }

  const handleTellerOperationDoubleClick = (event: React.MouseEvent<unknown>, row: ITellerOperation) => {
    const roleEntityTellerOperation = roleEntities.find(re => re.entityName === 'TellerOperation');
    if (isFalsy(roleEntityTellerOperation)) return;

    navigate(`/tellerOperation/${row.id}`);
  }

  type BillingUsage = 'billing' | 'new-billing';

  const [currentBillingUsage, setCurrentBillingUsage] = useState<BillingUsage>('billing');

  const handleAddBillings = (event: any) => {
    setCurrentBillingUsage('billing')
    setOpenBillingTypeFilter(true);
  }

  const handleAddArticleBillings = (event: any) => {
    setCurrentBillingUsage('billing')
    setOpenArticleBillingTypeFilter(true);
  }

  const handleAddNewBillings = (event: any) => {
    setCurrentBillingUsage('new-billing');
    setOpenBillingTypeFilter(true);
  }

  const handleAddNewArticleBillings = (event: any) => {
    setCurrentBillingUsage('new-billing');
    setOpenArticleBillingTypeFilter(true);
  }

  const handleAddNewTransportBillings = (event: any) => {

    const { isArrival, isDeparture, busDestinationId } = getValues();

    if (!isArrival && !isDeparture) {
      enqueueSnackbar(t('You have to specify departure or arrival'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    if (busDestinationId <= 0) {
      enqueueSnackbar(t('You have to specify destination'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setCurrentBillingUsage('new-billing');
    setOpenTransportBillingTypeFilter(true);
  }


  const handleOkBasicTextFilterBillingTypeForm = async () => {

    if (selectedFilterBillingTypes.length === 0) {
      enqueueSnackbar(t('No billing to add'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setOpenBillingTypeFilter(false);
      return;
    }

    await evaluateNewBillingsAdded(filteredBillingTypes, selectedFilterBillingTypes);
  }

  const evaluateNewBillingsAdded = async (billingTypes: IBillingType[], selectBillingTypes: number[]) => {
    if (billingTypes.some(x => x.isArticle)) {
      enqueueSnackbar(t('Articles can not be added here'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    if (billingTypes.some(x => x.isTransport)) {
      enqueueSnackbar(t('Transport can not be added here'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const billings2Append = billingTypes.filter(ct => selectBillingTypes.includes(ct.id || 0) &&
      getValues().newBillings.findIndex(b => b.billingTypeId === ct.id) < 0);

    const resultRegistration = await evaluateAddedBillings({
      ...getValues(),
      billings: billings2Append.map((x) => ({ ...x, billingTypeId: x.id, billingExtensions: [] }))
    });

    if (!resultRegistration.succeeded) {
      enqueueSnackbar(resultRegistration.messages.join('\n'), {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 3000
      });
      return;
    }

    if (currentBillingUsage === 'billing')
      (refAppendBillings.current ?? emptyFunc)(resultRegistration.data.billings);
    else
      (refAppendNewBillings.current ?? emptyFunc)(resultRegistration.data.billings);

    setOpenBillingTypeFilter(false);
  }



  const handleOkBasicTextFilterArticleBillingTypeForm = async () => {
    if (selectedFilterArticleBillingTypes.length === 0) {
      enqueueSnackbar(t('No billing to add'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setOpenArticleBillingTypeFilter(false);
      return;
    }

    await evaluateNewArticleBillingsAdded(filteredArticleBillingTypes, selectedFilterArticleBillingTypes);
  }

  const evaluateNewArticleBillingsAdded = async (billingTypes: IBillingType[], selectBillingTypes: number[]) => {
    if (billingTypes.some(x => !x.isArticle)) {
      enqueueSnackbar(t('Articles can not be added here'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const billings2Append = billingTypes.filter(art => selectBillingTypes.includes(art.deliveryArticleId || 0));

    const resultRegistration = await evaluateAddedBillings({
      ...getValues(),
      billings: billings2Append.map((x) => ({ ...x, billingTypeId: x.id, billingExtensions: [] }))
    });

    if (!resultRegistration.succeeded) {
      enqueueSnackbar(resultRegistration.messages.join('\n'), {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 3000
      });
      return;
    }

    const arrBillings = resultRegistration.data.billings.map((x) => {
      const articleBillingType = billings2Append.find(y => y.deliveryArticleId === x.deliveryArticleId);

      return {
        ...x, isArticle: true,
        deliveryArticleId: isFalsy(articleBillingType) ? 0 : articleBillingType!.deliveryArticleId,
        articleOption: isFalsy(articleBillingType) ? '' : articleBillingType!.articleOption
      }
    });

    if (currentBillingUsage === 'billing')
      (refAppendBillings.current ?? emptyFunc)(arrBillings);
    else
      (refAppendNewArticleBillings.current ?? emptyFunc)(arrBillings);

    setOpenArticleBillingTypeFilter(false);

  }

  const handleOkBasicTextFilterTransportBillingTypeForm = async () => {

    if (selectedFilterTransportBillingTypes.length === 0) {
      enqueueSnackbar(t('No billing to add'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setOpenArticleBillingTypeFilter(false);
      return;
    }

    evaluateNewTransportBillingsAdded(filteredTransportBillingTypes, selectedFilterTransportBillingTypes);
  }

  const evaluateNewTransportBillingsAdded = async (billingTypes: IBillingType[], selectBillingTypes: number[]) => {
    if (billingTypes.some(x => !x.isTransport)) {
      enqueueSnackbar(t('Only transports can be added here'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    const { busDestinationId, isArrival, isDeparture } = getValues();

    const billings2Append = billingTypes.filter(b => selectBillingTypes.includes(b.id || 0));

    const resultRegistration = await evaluateAddedBillings({
      ...getValues(),
      billings: billings2Append.map((x) => ({ ...x, isArrival, isDeparture, billingTypeId: x.id, billingExtensions: [] }))
    });

    if (!resultRegistration.succeeded) {
      enqueueSnackbar(resultRegistration.messages.join('\n'), {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 3000
      });
      return;
    }

    const arrBillings = resultRegistration.data.billings.map((x) => {
      //const transportBillingType = billings2Append.find(y => y.id === x.billingTypeId);      
      return { ...x, isArticle: false, busDestinationId, isArrival, isDeparture }
    });

    if (currentBillingUsage === 'billing')
      (refAppendBillings.current ?? emptyFunc)(arrBillings);
    else
      (refAppendNewTransportBillings.current ?? emptyFunc)(arrBillings);

    setOpenTransportBillingTypeFilter(false);
  }

  const handleClickOpenBusDestination = async (event: any) => {
    setOpenBusDestinationFilter(true);
  }

  const [openDepartureInfos, setOpenDepartureInfos] = useState<boolean>(false);
  const handleDepartureInfos = (event: any) => {
    setOpenDepartureInfos(true);
  }

  type PersonType = 'student' | 'parent';
  const [personType, setCurrentPersonType] = useState<PersonType>('student');
  const refPersonType = useRef<PersonType>('student');

  const handleClickOpenPerson = (personType: PersonType) => (event: any) => {
    setCurrentPersonType(personType);
    refPersonType.current = personType;
    setOpenPersonFilter(true);
  }

  const handleClickRemovePerson = (personType: PersonType) =>  (event: any) => {
    if(personType === 'student') {
      setValue('personId', 0);
      setValue('firstName', '');
      setValue('lastName', '');
      setValue('birthPlace', '');
      setValue('particularSign', '');
      setValue('civility', '');
      setValue('gender', '');
      setValue('birthCertificateNumber', '');
      setValue('civilRegistrar', '');
      setValue('birthCertificateDelivrancePlace', '');
      setValue('studentCode', '');

      setValue('fatherId', 0);
      setValue('motherId', 0);
      setValue('tutorId', 0);

      setValue('fatherFirstName', '');
      setValue('fatherLastName', '');
      setValue('fatherBirthPlace', '');
      setValue('motherFirstName', '');
      setValue('motherLastName', '');
      setValue('motherBirthPlace', '');
      setValue('tutorFirstName', '');
      setValue('tutorLastName', '');
      setValue('tutorBirthPlace', '');
    } else {
      setValue(`${currentParentType}Id` as FieldPath<IRegistration>, 0);
      setValue(`${currentParentType}FirstName` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}LastName` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}BirthPlace` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}ParticularSign` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}Civility` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}Gender` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}BirthCertificateNumber` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}CivilRegistrar` as FieldPath<IRegistration>, '');
      setValue(`${currentParentType}birthCertificateDelivrancePlace` as FieldPath<IRegistration>, '');      
    }
  }

  const [openParentPortables, setOpenParentPortables] = useState<boolean>(false);
  const [currentParentPortableIdx, setCurrentParentPortableIdx] = useState<number>(0);
  const [parentPortables, setParentPortables] = useState<IPerson[]>([]);
  const handleClickOpenParentPortables = async (portableIdx: number) => {

    const { personId } = getValues();
    if (personId <= 0) return;

    const prtPortables = await getParentPortables(personId);
    setParentPortables(prtPortables);
    setCurrentParentPortableIdx(portableIdx);
    setOpenParentPortables(true);
  }


  const handlePersonPortableDoubleClick = (event: React.MouseEvent<unknown>, row: IPerson) => {

    setOpenParentPortables(false);
    if (currentParentPortableIdx === 1)
      setValue('portable1', row.portable);
    else if (currentParentPortableIdx === 2)
      setValue('portable2', row.portable);
  }


  const [openParentEmails, setOpenParentEmails] = useState<boolean>(false);
  const [currentParentEmailIdx, setCurrentParentEmailIdx] = useState<number>(0);
  const [parentEmails, setParentEmails] = useState<IPerson[]>([]);
  const handleClickOpenParentEmails = async (emailIdx: number) => {

    const { personId } = getValues();
    if (personId <= 0) return;

    const prtEmails = await getParentEmails(personId);
    setParentEmails(prtEmails);
    setCurrentParentEmailIdx(emailIdx);
    setOpenParentEmails(true);
  }


  const handlePersonEmailDoubleClick = (event: React.MouseEvent<unknown>, row: IPerson) => {

    setOpenParentEmails(false);
    if (currentParentEmailIdx === 1)
      setValue('email1', row.email);
    else if (currentParentEmailIdx === 2)
      setValue('email2', row.email);
  }


  const debouncedNameChange = useRef(
    debounce(async () => {
      const { personId, firstName, lastName, particularSign } = getValues();

      if (personId > 0) return;

      if ((isFalsy(firstName) || firstName === '') && (isFalsy(lastName) || lastName === ''))
        return;

      const count = await getPersonsSearchCount(firstName, lastName, particularSign);

      if (!isFalsy(refPersonSnackbarId.current)) closeSnackbar(refPersonSnackbarId.current!);

      if (count > 0)
        refPersonSnackbarId.current = enqueueSnackbar(`${t('You have to check if person exists before save, verify it in this list of')} : ${count} ${t('_person')}(s)`, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' }, persist: true, //autoHideDuration : 5000,
          action: xActionPersonSearch(firstName, lastName, particularSign)
        });

    }, 1500)
  ).current;



  const refPersonSnackbarId = useRef<SnackbarKey>();
  const xActionPersonSearch = (firstName: string, lastName: string, particularSign: string): SnackbarAction => (snackbarId: SnackbarKey) => (
    <>
      <Button onClick={() => {
        // setUrlPdf(`${globalConfig.get().apiUrl}/Download/${fileToken}/${fileName}`); 

        setOpenPersonSearchFilter(true);
        closeSnackbar(snackbarId);
      }}>
        <GrSearch size={24} />
      </Button>
      <Button onClick={() => { closeSnackbar(snackbarId) }}>
        <GrClose size={24} />
      </Button>
    </>
  )

  useEffect(() => {
    debouncedNameChange();
  }, [watchFirstName, watchLastName, watchParticularSign, debouncedNameChange]);

  useEffect(() => {
    async function _() {
      const arr = await schoolYearServiceRef.current.getSchoolYearClassTemplates(watchSchoolYearId, false, '', '');

      setSchoolYearClassTemplates(arr);
      //setSchoolYearClassTemplates( arr.filter( x => x.studyLanguage === watchStudyLanguage) );
    }

    _();
  }, [watchSchoolYearId]);


  useEffect(() => {
    const arr = classes.filter(x => x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId
      && x.isActive && x.studyType === watchStudyType && x.studySection === watchStudySection && x.studyLanguage === watchStudyLanguage)
    if (arr.length === 1)
      setValue('schoolYearClassId', arr[0].id);
    // else if(arr.length > 0 && !arr.some(x => x.id === getValues().schoolYearClassId))
    //   setValue('schoolYearClassId', arr[0].id);

  }, [classes, watchSchoolYearClassTemplateId, watchStudyType, watchStudySection, watchStudyLanguage, setValue]);


  useEffect(() => {
    async function _() {
      // const schoolYear = (schoolYears || []).find(sy => sy.id === watchSchoolYearId);
      // const year = isFalsy(schoolYear) ? 0 : schoolYear.year;

      const studyTypes = (enumItems || []).filter(e => e.enumerationCode === Enum_STUDY_TYPE);
      if (studyTypes.length === 1)
        setValue('studyType', studyTypes[0].code);

      if (watchSchoolYearId > 0 /*&& watchSchoolYearClassTemplateId > 0*/) {
        const arr = await schoolYearServiceRef.current.getSchoolYearClasses({
          schoolYearId: watchSchoolYearId,
          name: '',
          studyType: '', //watchStudyType, 
          studySection: '',
          studyLanguage: '', //watchStudyLanguage,
          studyLevel: ''
        });

        setClasses(arr);
        // setClasses(arr.filter(x => x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId && x.isActive) );  
      }
    }

    _();
  }, [enumItems, setValue, watchSchoolYearId]);


  const [schoolYearClassCountStudents, setSchoolYearClassCountStudents] = useState<number>(0);
  const [generateIntialRegistrationLoading, setGenerateIntialRegistration] = useState<boolean>(false);
  useEffect(() => {
    async function _() {

      const { status, initialSchoolYearClassId } = getValues();

      const schoolYearClass = classes.find(c => c.id === watchSchoolYearClassId);

      setSchoolYearClassCountStudents(isFalsy(schoolYearClass) ? 0 : schoolYearClass.countStudents);

      if (status === '95' && watchSchoolYearClassId > 0 /*&& watchSchoolYearClassId !== initialSchoolYearClassId*/) {
        try {
          setGenerateIntialRegistration(true);
          const registration = await generateIntialRegistration({ ...getValues() });
          // to do, just set billing amount according to billing id and billing type id.
          reset({ ...registration });
        } catch {

        } finally {
          setGenerateIntialRegistration(false);
        }

      } else {
        setValue('initialSchoolYearClassId', 0);
      }

    }

    _();
  }, [watchSchoolYearClassId, watchPersonId, watchStudentFrom]);


  useEffect(() => {
    async function _() {
      if(watchFatherId <= 0 || ( (getValues().fatherFirstName||'') !== '' && (getValues().fatherLastName||'') !== '')) 
        return;      
      await loadParent('father', watchFatherId);      
    }

    _();
  }, [watchFatherId]);

  useEffect(() => {
    async function _() {
      if(watchMotherId <= 0 || ( (getValues().motherFirstName||'') !== '' && (getValues().motherLastName||'') !== '')) 
        return;      
      await loadParent('mother', watchMotherId);      
    }

    _();
  }, [watchMotherId]);

  useEffect(() => {
    async function _() {
      if(watchTutorId <= 0 || ( (getValues().tutorFirstName||'') !== '' && (getValues().tutorLastName||'') !== '')) 
        return;      
      await loadParent('tutor', watchTutorId);      
    }

    _();
  }, [watchTutorId]);


  useEffect(() => {
    setValue('civility', watchGender === 'M' ? 'Mr' :  watchGender === 'F' ? 'Mme' : '' );
    setValue('fatherCivility', watchFatherGender === 'M' ? 'Mr' :  watchFatherGender === 'F' ? 'Mme' : '' );
    setValue('motherCivility', watchMotherGender === 'M' ? 'Mr' :  watchMotherGender === 'F' ? 'Mme' : '' );
    setValue('tutorCivility', watchTutorGender === 'M' ? 'Mr' :  watchTutorGender === 'F' ? 'Mme' : '' );
  }, [watchGender, watchFatherGender, watchMotherGender, watchTutorGender]);
  

  const loadParent = async (parentType: ParentType, parentId: number) => {

    const parent = await retrieveEntity('Person', parentId) as IPerson;

    const {gender, civility, firstName, lastName, birthDate, birthPlace,
            particularSign, birthCertificateNumber, civilRegistrar, 
            portable1, portable2, email1, email2
        } = parent;

      setValue(`${parentType}Gender` as FieldPath<IRegistration>, gender as any);
      setValue(`${parentType}Civility` as FieldPath<IRegistration>, civility as any);
      setValue(`${parentType}FirstName` as FieldPath<IRegistration>, firstName as any);
      setValue(`${parentType}LastName` as FieldPath<IRegistration>, lastName as any);
      setValue(`${parentType}BirthDate` as FieldPath<IRegistration>, birthDate as any);
      setValue(`${parentType}BirthPlace` as FieldPath<IRegistration>, birthPlace as any);

      setValue(`${parentType}ParticularSign` as FieldPath<IRegistration>, particularSign as any);
      setValue(`${parentType}BirthCertificateNumber` as FieldPath<IRegistration>, birthCertificateNumber as any);
      setValue(`${parentType}CivilRegistrar` as FieldPath<IRegistration>, civilRegistrar as any);
      setValue(`${parentType}Name` as FieldPath<IRegistration>, lg.includes('fr') ? `${lastName||''} ${firstName||''}`.trim() : 
                  `${firstName||''} ${lastName||''}`.trim() as any);
      setValue(`${parentType}Portable1` as FieldPath<IRegistration>, portable1 as any);
      setValue(`${parentType}Portable2` as FieldPath<IRegistration>, portable2 as any);
      setValue(`${parentType}Email1` as FieldPath<IRegistration>, email1 as any);
      setValue(`${parentType}Email2` as FieldPath<IRegistration>, email2 as any);
  }



  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;
    const studyTypes = (enumItems || []).filter(x => x.enumerationCode === Enum_STUDY_TYPE);
    if (getValues().status === '95' && studyTypes.length === 1)
      setValue('studyType', studyTypes[0].code);

    const studySections = (enumItems || []).filter(x => x.enumerationCode === Enum_STUDY_SECTION);
    if (getValues().studySection === '' && studySections.length > 0)
      setValue('studySection', studySections[0].code);

    const studyLanguages = (enumItems || []).filter(x => x.enumerationCode === Enum_STUDY_LANGUAGE);
    if (getValues().studyLanguage === '' && studyLanguages.length > 0)
      setValue('studyLanguage', studyLanguages[0].code);

  }, [enumItems, getValues, setValue])


  useEffect(() => {
    if (getValues().schoolYearId <= 0)
      setValue('schoolYearId', applicationSetup.currentSchoolYearId);
  }, []);


  useEffect(() => {
    setCurrentFormNameAtom(t('Registration'));
    setCurrentBasicTextFilterProps(basicFilterRegistration);
  }, []);

  

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    if (_id > 0)
      retrieveData('Registration', _id, refetch);
  }, [_id]);


  useEffect(() => {

    if (_data && _data.id > 0) {
      setCurrentFormNameAtom(`${t('Registration')} - # ${_data.id} # - ${_data.isRepeater ? `(${t('Repeater')})` : ''}`);
      
      reset({
        ..._data,
        initialSchoolYearClassId: _data.schoolYearClassId,
        isArrival: false, isDeparture: false
      });
    }
  }, [_data]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    
    setCurrentFormNameAtom(t('Registration'));
    reset({
      ...defaultRegistration, studyType: getValues().studyType,
      schoolYearId: applicationSetup.currentSchoolYearId
    });
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!checkEntitySaveAuthorization('Registration', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();
    if (data.firstName.trim() === '' && data.lastName.trim() === '') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.billings.length === 0) {
      enqueueSnackbar(t('Invalid data !!!'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (isFalsy(data.studyType)) {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    mutate(data);
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('Registration', _id);
  }

  const [openNewArticleBilling, setOpenNewArticleBilling] = useState<boolean>(false);
  const actionDataCreateNewArticleBillingsRegistration = async (event: MouseEvent<HTMLButtonElement>) => {
    const { status } = getValues();
    if (status !== '10') {
      enqueueSnackbar(t('This is possible on validated registration'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledNewArticle(false);
    setOpenNewArticleBilling(true);
  }

  const [okButtonDisabledNewArticle, setOkButtonDisabledNewArticle] = useState<boolean>(false);
  const handleOkCreateNewArticleBillingsRegistration = async () => {
    const { id, newArticleBillings } = getValues();
    if (newArticleBillings.length === 0) {
      enqueueSnackbar(t('No billing to add'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledNewArticle(true);
    mutateNewArticles({ id, billings: newArticleBillings });
  }

  const [openNewTransportBilling, setOpenNewTransportBilling] = useState<boolean>(false);
  const actionDataCreateNewTransportBillingsRegistration = async (event: MouseEvent<HTMLButtonElement>) => {
    const { status } = getValues();
    if (status !== '10') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledNewTransport(false);
    setOpenNewTransportBilling(true);
  }

  const [okButtonDisabledNewTransport, setOkButtonDisabledNewTransport] = useState<boolean>(false);
  const handleOkCreateNewTransportBillingsRegistration = async () => {
    const { id, newTransportBillings } = getValues();
    if (newTransportBillings.length === 0) {
      enqueueSnackbar(t('No billing to add'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledNewTransport(true);
    mutateNewTransports({ id, billings: newTransportBillings });
  }

  const [openNewBilling, setOpenNewBilling] = useState<boolean>(false);
  const actionDataCreateNewBillingsRegistration = async (event: MouseEvent<HTMLButtonElement>) => {
    const { status, newBillings } = getValues();
    if (status !== '10') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledNewBilling(false);
    setOpenNewBilling(true);
  }

  const [okButtonDisabledNewBilling, setOkButtonDisabledNewBilling] = useState<boolean>(false);
  const handleOkCreateNewBillingsRegistration = async () => {
    const { id, newBillings } = getValues();
    if (newBillings.length === 0) {
      enqueueSnackbar(t('No billing to add'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    setOkButtonDisabledNewBilling(true);
    mutateNewBillings({ id, billings: newBillings });
  }

  const [openChangeClass, setOpenChangeClass] = useState<boolean>(false); // for open dialog to select class
  const actionDataChangeClassRegistration = async (event: MouseEvent<HTMLButtonElement>) => {
    setOpenChangeClass(true);
  }

  const handleOkChangeClassAction = async () => {
    const { id, schoolYearClassId } = getValues();
    if (schoolYearClassId <= 0) {
      enqueueSnackbar(t('Invalid data !!!'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    mutateChangeClass({ id, newClassId: schoolYearClassId });
  }

  const [openResetClass, setOpenResetClass] = useState<boolean>(false); // for open dialog to select subject exam
  const actionDataResetClassRegistration = async (event: MouseEvent<HTMLButtonElement>) => {

    setOpenResetClass(true);
  }

  const handleOkResetClassAction = async () => {
    const { id, schoolYearClassId } = getValues();
    if (schoolYearClassId <= 0) {
      enqueueSnackbar(t('Invalid data !!!'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      return;
    }

    mutateResetClass({ id, newClassId: schoolYearClassId });
  }


  const printData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityPrintDrawer('Registration', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    //console.log('after action !!!');
    //queryClient.invalidateQueries(['Registration', _id]);
    await retrieveData('Registration', _id, refetch);
    //reset(_data);
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={0.5} columnSpacing={0.1}>
          <Grid item xs={12} md={6} lg={6} component={Paper} elevation={3} sx={{ borderRadius: 3, ml: 0, }} >
            <Stack flexDirection='column'  >
              {(getValues().hasDeparted) &&
                <Box sx={{ mt: 0.25, width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Link href="#" onClick={handleDepartureInfos}
                    sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                    {`${t('This student is no longer in the school')} : ${t('More details here')} ... `}
                  </Link>
                </Box>}
              <Box sx={{ mt: 1, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('School year'))} - ${t('Study type')}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnPrint' onClick={printData} sx={{ display: 'none' }} />

                <Button id='btnActionCreateNewArticleBillingsRegistration' onClick={actionDataCreateNewArticleBillingsRegistration} sx={{ display: 'none' }} />
                <Button id='btnActionCreateNewTransportBillingsRegistration' onClick={actionDataCreateNewTransportBillingsRegistration} sx={{ display: 'none' }} />
                <Button id='btnActionCreateNewBillingsRegistration' onClick={actionDataCreateNewBillingsRegistration} sx={{ display: 'none' }} />

                <Button id='btnActionChangeClassRegistration' onClick={actionDataChangeClassRegistration} sx={{ display: 'none' }} />
                <Button id='btnActionResetClassRegistration' onClick={actionDataResetClassRegistration} sx={{ display: 'none' }} />

                <TextField sx={{ width: 'calc(15% - 8px)' }} id="id" label={t('Code')}
                  {...register('registrationCode')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <Controller name='schoolYearId' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('School year')} inputProps={{ readOnly: false }} focused >
                      {schoolYears && schoolYears.map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='studyType' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(35% - 8px)' }} id="schoolYearId"
                      label={t('Study type')} inputProps={{ readOnly: false }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_TYPE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

              </Box>
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap key={` student code : ${getValues().studentCode}`}
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Student'))} - ${t('Information')} :: `} <Link
                    href="#"
                    onClick={() => { if (roleEntities.some(r => r.entityName === 'Student')) navigate(`/student/${getValues().id}`); }}

                    sx={{ textDecoration: 'none', color: 'blue' }} // optional: remove underline
                  >
                    {getValues().studentCode}
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} key={` person ${getValues().personId}`}>

                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label={t('Gender')}                      
                      sx={{ width: '15%' }}
                      {...field}
                    >
                      {(enumItems2 || [])
                        .filter(e => e.enumerationCode === Enum_PERSON_GENDER)
                        .map(e => (
                          <MenuItem key={e.code} value={e.code}>
                            {e.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
                <Controller
                  name="civility"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      select
                      label={t('Civility')}
                      inputProps={{ readOnly: true }}
                      sx={{ width: '15%' }}
                      {...field}
                    >
                      {(enumItems2 || [])
                        .filter(e => e.enumerationCode === Enum_PERSON_CIVILITY)
                        .map(e => (
                          <MenuItem key={e.code} value={e.code}>
                            {e.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />

                <TextField sx={{ width: 'calc(35% - 8px)' }} id="fn" label={lg.startsWith('fr') ? t('Last name') : t('First name')}
                  {...register(lg.startsWith('fr') ? 'lastName' : 'firstName')}
                  inputProps={{ readOnly: (watchPersonId > 0), style: { textTransform: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {(isFalsy(watchPersonId) || watchPersonId <= 0) ?
                          <IconButton color="primary" onClick={handleClickOpenPerson('student')}>
                            <ArrowDropDownCircleIcon />
                          </IconButton> :
                          <IconButton color="primary" onClick={handleClickRemovePerson('student')}>
                            <RemoveCircleIcon />
                          </IconButton>
                        }
                      </InputAdornment>
                    )
                  }}
                  helperText={(getValues().personId > 0 && roleEntities.find(re => re.entityName === 'Person')) ?
                    <FormHelperText>
                      <Link
                        href="#"
                        onClick={() => {
                          if (roleEntities.some(e => e.entityName === 'Person'))
                            navigate(`/person/${getValues().personId}`)
                        }}
                        sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                      >
                        {`${t('View details')} : ${getValues().personId}`}
                      </Link>
                    </FormHelperText> : null
                  } />
                <TextField sx={{ width: 'calc(35% - 8px)' }} id="ln" label={lg.startsWith('fr') ? t('First name') : t('Last name')}
                  {...register(lg.startsWith('fr') ? 'firstName' : 'lastName')} inputProps={{ readOnly: (watchPersonId > 0), style: { textTransform: 'none' } }} />

                {openPersonFilter && <FormDialog open={openPersonFilter} maxWidth='md'
                  okText='' cancelText='' title={t('Person')} onCancel={() => { }}
                  onClose={() => { setOpenPersonFilter(false); }} onOk={() => { setOpenPersonFilter(false); }}  >
                  <BasicTextFilterForm<IPerson> {...basicFilterPerson} />
                </FormDialog>}
                {openPersonSearchFilter && <FormDialog open={openPersonSearchFilter} maxWidth='md'
                  okText='' cancelText='' title={t('Person')} onCancel={() => { }}
                  onClose={() => { setOpenPersonSearchFilter(false); }} onOk={() => { setOpenPersonSearchFilter(false); }}  >
                  <BasicTextFilterForm<IPerson> {...basicFilterPersonSearch} />
                </FormDialog>}
                {openDepartureInfos && <FormDialog open={openDepartureInfos} maxWidth='sm' height='45vh'
                  okText='' cancelText='' title={t('Person')} onCancel={() => { }}
                  onClose={() => { setOpenDepartureInfos(false); }} onOk={() => { setOpenDepartureInfos(false); }}  >
                  <Stack flexDirection='column'  >
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='departureType' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(100% - 8px)' }} id="departureType"
                            label={t('Type')} inputProps={{ readOnly: false }} >
                            <MenuItem key='EXPEL' value='EXPEL'>{t('Expelled')}</MenuItem>
                            <MenuItem key='WITHD' value='WITHD'>{t('Withdrawn')}</MenuItem>
                          </TextField>
                        )}
                      />
                      <Controller control={control}
                        name='departureDate'
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                          <DatePicker label={t('Date')}
                            readOnly={(watchPersonId > 0)}
                            onChange={onChange}
                            value={new Date(value)}
                            slotProps={{ textField: { sx: { width: 'calc(50% - 8px)' } } }}
                          //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                          />)}
                      />
                      <Controller name='departurePurpose' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(100% - 8px)' }} id="departurePurpose"
                            label={t('Purpose')} inputProps={{ readOnly: true }} >
                            {enumItems2 && enumItems2.filter(e =>
                              (e.enumerationCode === Enum_REGISTRATION_WITHDRAWAL && getValues().departureType === 'WITHD') ||
                              (e.enumerationCode === Enum_REGISTRATION_EXPEL && getValues().departureType === 'EXPEL')).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />
                      <TextField sx={{ width: 'calc(100% - 8px)' }} id="departureDescription" label={t('Description')} multiline rows={4}
                        {...register('departureDescription')} inputProps={{ readOnly: true, style: { textTransform: 'none' } }} />
                    </Box>
                  </Stack>
                </FormDialog>}
              </Box>
              <Box sx={{ mt: 0.25, width: '100%', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap' }}>
                <Controller
                  name="birthDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      label={t('Birth date')}
                      readOnly={watchPersonId > 0}
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: '20%' } } }}
                    />
                  )}
                />
                <TextField
                  sx={{ width: 'calc(30% - 8px)' }}
                  id="birthPlace"
                  label={t('Birth place')}
                  {...register('birthPlace')}
                  inputProps={{ readOnly: watchPersonId > 0 }}
                />

                {/* birthPlace + More Info button inline */}
                <Box sx={{ display: 'flex', alignItems: 'center', width: 'auto', flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    id="birthCertificateNumber"
                    label={t('Birth certif. N°')}
                    {...register('birthCertificateNumber')}
                    inputProps={{ readOnly: watchPersonId > 0 }}
                  />
                  <TextField
                    fullWidth
                    id="civilRegistrar"
                    label={t('Civil registar')}
                    {...register('civilRegistrar')}
                    inputProps={{ readOnly: watchPersonId > 0 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenMorePersonInfo(true)}
                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                  >
                    {t('Details')}
                  </Button>
                </Box>
              </Box>
              {openMorePersonInfo && <FormDialog open={openMorePersonInfo} maxWidth='md'
                okText={t('Close')} cancelText='' title={`${t('More infos')} - ${getValues('firstName')} ${getValues('lastName')} `} onCancel={() => { }}
                onClose={() => { setOpenMorePersonInfo(false); }} onOk={() => { setOpenMorePersonInfo(false); }}  >
                <Stack flexDirection='column'  >
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <TextField sx={{ width: 'calc(25% - 8px)' }} id="particularSign" label={t('Part. sign')}
                      {...register('particularSign')} />
                    <TextField sx={{ width: 'calc(25% - 8px)' }} id="birthCertificateDelivrancePlace"
                      label={t('Civil state center')}
                      {...register('birthCertificateDelivrancePlace')} />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                      {`${t('Biometric informations and others')}`}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Controller name='bloodType' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="defaultLanguage"
                          label={t('Blood type')} inputProps={{ readOnly: false }}  >
                          <MenuItem value='O'>O</MenuItem>
                          <MenuItem value='A'>A</MenuItem>
                          <MenuItem value='B'>B</MenuItem>
                          <MenuItem value='AB'>AB</MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller name='rhesusFactor' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="defaultLanguage"
                          label={t('Rhesus factor')} inputProps={{ readOnly: false }}  >
                          <MenuItem value='+'>+</MenuItem>
                          <MenuItem value='-'>-</MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller name='chronicIllnessCode' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="chronicIllnessCode"
                          label={t('Chronic illness')} inputProps={{ readOnly: false }}
                          helperText={
                            <FormHelperText>
                              {canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_CHRONIC_ILLNESS,
                                `${t('Chronic illness')}`, `${t('Chronic illness')}`)}
                                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                {t('Add')}
                              </Link>}
                              {(canUpdateEnumerationItem && !isFalsy(watchChronicIllnessCode)) && <Link href="#" onClick={(event) => {
                                onUpdateItemClick(Enum_CHRONIC_ILLNESS, `${t('Chronic illness')}`, `${t('Chronic illness')}`,
                                  refEnumItems.current?.find(x => x.enumerationCode === Enum_CHRONIC_ILLNESS && x.code === watchChronicIllnessCode)
                                );
                              }}
                                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                {t('Update')}
                              </Link>}
                            </FormHelperText>
                          }>
                          {enumItems && enumItems.filter(e =>
                            e.enumerationCode === Enum_CHRONIC_ILLNESS).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <FormControlLabel sx={{ width: 'calc(40% - 8px)' }}
                      label={`${t('Has specific needs ?')}`}
                      control={
                        <Controller
                          name='hasSpecificNeeds'
                          control={control}
                          render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                        />} />
                    <Controller name='specificNeedPurposeCode' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(60% - 8px)' }} id="specificNeedPurposeCode"
                          label={t('Purpose')} inputProps={{ readOnly: !watchHasSpecificNeeds }}
                          helperText={
                            <FormHelperText>
                              {canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_SPECIFIC_NEED_PURPOSE,
                                `${t('Specific needs')} : ${t('Purpose')}`, `${t('Specific needs')} : ${t('Purpose')}`)}
                                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                {t('Add')}
                              </Link>}
                              {(canUpdateEnumerationItem && !isFalsy(watchSpecificNeedPurposeCode)) && <Link href="#" onClick={(event) => {
                                onUpdateItemClick(Enum_SPECIFIC_NEED_PURPOSE, `${t('Specific needs')} : ${t('Purpose')}`, `${t('Specific needs')} : ${t('Purpose')}`,
                                  refEnumItems.current?.find(x => x.enumerationCode === Enum_SPECIFIC_NEED_PURPOSE && x.code === watchSpecificNeedPurposeCode)
                                );
                              }}
                                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                {t('Update')}
                              </Link>}
                            </FormHelperText>
                          }>
                          {enumItems && enumItems.filter(e =>
                            e.enumerationCode === Enum_SPECIFIC_NEED_PURPOSE).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <FormControlLabel sx={{ width: 'calc(40% - 8px)' }}
                      label={`${t('Has disability ?')}`}
                      control={
                        <Controller
                          name='hasDisability'
                          control={control}
                          render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                        />} />
                    <Controller name='disabilityType' control={control} 
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(60% - 8px)' }} id="disabilityType"
                          label={t('Disability')} inputProps={{ readOnly: !watchHasDisability }}
                          helperText={
                            <FormHelperText>
                              {canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_DISABILITY_TYPE,
                                `${t('Disability type')}`, `${t('Disability type')}`)}
                                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                {t('Add')}
                              </Link>}
                              {(canUpdateEnumerationItem && !isFalsy(watchDisabilityType)) && <Link href="#" onClick={(event) => {
                                onUpdateItemClick(Enum_DISABILITY_TYPE, `${t('Disability type')}`, `${t('Disability type')}`,
                                  refEnumItems.current?.find(x => x.enumerationCode === Enum_DISABILITY_TYPE && x.code === watchDisabilityType)
                                );
                              }}
                                sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                {t('Update')}
                              </Link>}
                            </FormHelperText>
                          }>
                          {enumItems && enumItems.filter(e =>
                            e.enumerationCode === Enum_DISABILITY_TYPE).map(
                              (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <ToggleButtonGroup size="small"
                      color="primary"
                      value={currentParentType}
                      exclusive
                      onChange={(event, parentType) => handleChangeCurrentParentType(event, parentType)}
                      aria-label="Small sizes" fullWidth
                    >
                      {
                        [{ value: 'father', name: t('_Father') },
                        { value: 'mother', name: t('_Mother') },
                        { value: 'tutor', name: t('_Tutor') }
                        ].map(x =>
                          <ToggleButton value={x.value} key={x.value}>
                            {x.name}
                          </ToggleButton>
                        )
                      }
                    </ToggleButtonGroup>

                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} key={` person ${currentParentType} name`}>
                    <Controller
                      name={`${currentParentType}Gender`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          select
                          label={t('Gender')}
                          sx={{ width: '15%' }}
                          {...field}
                        >
                          {(enumItems2 || [])
                            .filter(e => e.enumerationCode === Enum_PERSON_GENDER)
                            .map(e => (
                              <MenuItem key={e.code} value={e.code}>
                                {e.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      name={`${currentParentType}Civility`}
                      control={control}
                      render={({ field }) => (
                        <TextField key={`${currentParentType} civility`}
                          select
                          label={t('Civility')}
                          inputProps={{ readOnly: true }}
                          sx={{ width: '15%' }}
                          {...field}
                        >
                          {(enumItems2 || [])
                            .filter(e => e.enumerationCode === Enum_PERSON_CIVILITY)
                            .map(e => (
                              <MenuItem key={e.code} value={e.code}>
                                {e.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="fn" key={`${currentParentType} 1 fn`}
                        label={lg.startsWith('fr') ? t('Last name') : t('First name')}
                      {...register(`${currentParentType}${lg.startsWith('fr') ? 'LastName' : 'FirstName'}`)}
                      inputProps={{ readOnly: ((getValues(`${currentParentType}Id` as any) as number) > 0), 
                                    style: { textTransform: 'none' } }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            {(isFalsy(getValues(`${currentParentType}Id` as any)) || 
                                (getValues(`${currentParentType}Id` as any) as number) <= 0) ?
                              <IconButton color="primary" onClick={handleClickOpenPerson('parent')}>
                                <ArrowDropDownCircleIcon />
                              </IconButton> :
                              <IconButton color="primary" onClick={handleClickRemovePerson('parent')}>
                                <RemoveCircleIcon />
                              </IconButton>
                            }
                          </InputAdornment>
                        )
                      }}
                      helperText={(getValues(`${currentParentType}Id` as any) as number) > 0 ?
                        <FormHelperText>{(getValues(`${currentParentType}Id` as any) as number)}</FormHelperText> : null
                      } />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="ln" key={`${currentParentType} ln`}
                      label={lg.startsWith('fr') ? t('First name') : t('Last name')}
                      {...register(`${currentParentType}${lg.startsWith('fr') ? 'FirstName' : 'LastName'}`)} 
                      inputProps={{ readOnly: ( (getValues(`${currentParentType}Id` as any) as number) > 0), 
                                    style: { textTransform: 'none' } }} />
                  </Box>
                  <Box key={` person ${currentParentType} birth date`}
                      sx={{ mt: 0.25, width: '100%', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap' }}>
                    <Controller
                      name={`${currentParentType}BirthDate`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          label={t('Birth date')}
                          readOnly={(getValues(`${currentParentType}Id` as any) as number) > 0}
                          onChange={onChange}
                          value={new Date(value)}
                          slotProps={{ textField: { sx: { width: '20%' } } }}
                        />
                      )}
                    />
                    <TextField
                      sx={{ width: 'calc(25% - 8px)' }}
                      id="birthPlace"
                      label={t('Birth place')}
                      {...register(`${currentParentType}BirthPlace`)}
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                    />
                    <TextField
                      sx={{ width: 'calc(20% - 8px)' }}
                      id="particularSign"
                      label={t('Particular sign')}
                      {...register(`${currentParentType}ParticularSign`)}
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                    />
                    
                      <TextField
                        sx={{ width: 'calc(15% - 8px)' }}
                        id="birthCertificateNumber"
                        label={t('Birth certif. N°')}
                        {...register(`${currentParentType}BirthCertificateNumber`)}
                        inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                      />
                      <TextField
                        sx={{ width: 'calc(20% - 8px)' }}
                        id="civilRegistrar"
                        label={t('Civil registar')}
                        {...register(`${currentParentType}CivilRegistrar` )}
                        inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0 }}
                      />
                    
                  </Box>

                  <Box sx={{ mt: 1, width: '100%' }} key={` person ${currentParentType} contact`}>
                    <TextField sx={{ width: 'calc(15% - 8px)' }} id="portable1" label={`${t('Portable')} 1`}
                      {...register(`${currentParentType}Portable1`)} 
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0, 
                                    autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                    <TextField sx={{ width: 'calc(15% - 8px)' }} id="portable2" label={`${t('Portable')} 2`}
                      {...register(`${currentParentType}Portable2`)} 
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0, 
                                    autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="email1" label={`${t('Email')} 1`}
                      {...register(`${currentParentType}Email1`)} 
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0, 
                                    autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                    <TextField sx={{ width: 'calc(35% - 8px)' }} id="email2" label={`${t('Email')} 2`}
                      {...register(`${currentParentType}Email2`)} 
                      inputProps={{ readOnly: (getValues(`${currentParentType}Id` as any) as number) > 0,
                                    autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                  </Box>

                </Stack>
              </FormDialog>}
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Class'))} `}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='studySection' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="studySection"
                      label={t('Study section')} inputProps={{ readOnly: false }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_SECTION).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='studyLanguage' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="studyLanguage"
                      label={t('Study language')} inputProps={{ readOnly: false }} focused >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_LANGUAGE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='schoolYearClassTemplateId' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="schoolYearClassTemplateId"
                      label={t('Study level')} inputProps={{ readOnly: false }} focused >
                      <MenuItem key={0} value={0}>::::</MenuItem>
                      {schoolYearClassTemplates.filter(x => x.studySection === watchStudySection && x.studyLanguage === watchStudyLanguage).map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.classTemplateName}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='schoolYearClassId' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="schoolYearClassId"
                      label={t('Class')} inputProps={{ readOnly: false }} focused
                      helperText={
                        <FormHelperText>
                          <Link
                            href="#"
                            onClick={() => {
                              if (roleEntities.some(e => e.entityName === 'SchoolYearClass'))
                                navigate(`/schoolYearClass/${getValues().schoolYearClassId}`)
                            }}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
                          >
                            {isFalsy(schoolYearClassCountStudents) ? '' :
                              `# ${t('Student')}(s) : ${schoolYearClassCountStudents} `}
                          </Link>
                        </FormHelperText>
                      }
                    >
                      {classes.filter(x => x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId
                        && x.isActive && x.studyType === watchStudyType && x.studySection === watchStudySection && x.studyLanguage === watchStudyLanguage).map(
                          (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                  label={t('Repeater ?')}
                  control={
                    <Controller
                      name='isRepeater'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
                <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                  label={t('Old ?')}
                  control={
                    <Controller
                      name='isOldStudent'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
                <Controller name='studentFrom' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studentFrom"
                      label={t('Student comming from')} inputProps={{ readOnly: false }}
                      helperText={
                        <FormHelperText>
                          {canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_STUDENT_FROM,
                            t('Student comming from'), t('Student comming from'))}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                            {t('Add')}
                          </Link>}
                          {(canUpdateEnumerationItem && !isFalsy(watchStudentFrom)) && <Link href="#" onClick={(event) => {
                            onUpdateItemClick(Enum_STUDENT_FROM, t('Student comming from'), t('Student comming from'),
                              refEnumItems.current?.find(x => x.enumerationCode === Enum_STUDENT_FROM && x.code === watchStudentFrom)
                            );
                          }}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                            {t('Update')}
                          </Link>}
                        </FormHelperText>
                      }>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDENT_FROM).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                  label={t('Social case ?')}
                  control={
                    <Controller
                      name='isSocialCase'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
                <Controller name='socialCaseReason' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="socialCaseReason"
                      key={`${watchIsSocialCase} :: social case`}
                      label={t('Case reason')} inputProps={{ readOnly: !watchIsSocialCase }}
                      helperText={
                        <FormHelperText>
                          {canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_SOCIAL_CASE_REASON,
                            t('Social case reason'), t('Social case reason'))}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                            {t('Add')}
                          </Link>}
                          {(canUpdateEnumerationItem && !isFalsy(watchSocialCaseReason)) && <Link href="#" onClick={(event) => {
                            onUpdateItemClick(Enum_SOCIAL_CASE_REASON, t('Social case reason'), t('Social case reason'),
                              refEnumItems.current?.find(x => x.enumerationCode === Enum_SOCIAL_CASE_REASON && x.code === watchSocialCaseReason)
                            );
                          }}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                            {t('Update')}
                          </Link>}
                        </FormHelperText>
                      }>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_SOCIAL_CASE_REASON).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='status' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="status"
                      label={t('Status')} inputProps={{ readOnly: true }}
                      helperText={getValues().statusDate ? `${t('Status date')}: ${new Intl.DateTimeFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(new Date(getValues().statusDate))}` : ''}
                    >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_REGISTRATION_STATUS).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

                <Controller control={control}
                  name='effectiveDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Effective date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(18% - 8px)'}} />}
                    />)}
                />
              </Box>
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Registration'))} - ${t('Information')}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                {/* <FormControlLabel sx={{width:'calc(25% - 8px)'}}
                                  label={t('Bus ?')}
                                  control={
                                  <Controller
                                      name='arriveOrGoesByBus'
                                      control={control}
                                      render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                  />} /> */}

                <TextField sx={{ width: 'calc(50% - 8px)' }} id="portable1" label={`${t('Portable')}(1)`}
                  {...register('portable1')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={() => handleClickOpenParentPortables(1)}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }} />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="portable2" label={`${t('Portable')}(2)`}
                  {...register('portable2')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={() => handleClickOpenParentPortables(2)}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }} />

                {openParentPortables && <FormDialog open={openParentPortables} maxWidth='md'
                  okText={''} cancelText={t('Cancel')} title={t('Portable')} onCancel={() => { setOpenParentPortables(false); }}
                  onClose={() => { setOpenParentPortables(false); }} onOk={() => { setOpenParentPortables(false); }}  >
                  <EnhancedTable<IPerson> rows={parentPortables}
                    headCells={[
                      { id: 'id', label: t('Id'), display: true, type: 'numeric', },
                      { id: 'firstName', label: t('First name'), display: true, type: 'string', },
                      { id: 'lastName', label: t('Last name'), display: true, type: 'string', },

                      { id: 'portable', label: t('Portable'), display: true, type: 'string', },
                    ]}
                    title={`${t('Portable')}`} objKey='portable'
                    //stateSelected={[filteredSchoolYearTemplateSubjects, setFilteredSchoolYearTemplateSubjects]} 
                    onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                    onRowDoubleClick={handlePersonPortableDoubleClick}
                    rowActionIcon={undefined}
                    toolbarActions={[
                      // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                    ]}
                  />
                </FormDialog>}
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >

                {/* <FormControlLabel sx={{width:'calc(25% - 8px)'}}
                                  label={t('Brunch ?')}
                                  control={
                                  <Controller
                                      name='takeBrunch'
                                      control={control}
                                      render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                  />} /> */}
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="email1" label={`${t('Email')}(1)`}
                  {...register('email1')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={() => handleClickOpenParentEmails(1)}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }} />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="email2" label={`${t('Email')}(2)`}
                  {...register('email2')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={() => handleClickOpenParentEmails(2)}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }} />

                {openParentEmails && <FormDialog open={openParentEmails} maxWidth='md'
                  okText={''} cancelText={t('Cancel')} title={t('Email')} onCancel={() => { setOpenParentEmails(false); }}
                  onClose={() => { setOpenParentEmails(false); }} onOk={() => { setOpenParentEmails(false); }}  >
                  <EnhancedTable<IPerson> rows={parentEmails}
                    headCells={[
                      { id: 'id', label: t('Id'), display: true, type: 'numeric', },
                      { id: 'firstName', label: t('First name'), display: true, type: 'string', },
                      { id: 'lastName', label: t('Last name'), display: true, type: 'string', },

                      { id: 'email', label: t('Email'), display: true, type: 'string', },
                    ]}
                    title={`${t('Email')}`} objKey='email'
                    //stateSelected={[filteredSchoolYearTemplateSubjects, setFilteredSchoolYearTemplateSubjects]} 
                    onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                    onRowDoubleClick={handlePersonEmailDoubleClick}
                    rowActionIcon={undefined}
                    toolbarActions={[
                      // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                    ]}
                  />
                </FormDialog>}
              </Box>
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Parent'))} - ${t('_Tutor')}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(34% - 8px)' }} id="fatherName" label={`${t('_Father')}`}
                  {...register('fatherName')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(33% - 8px)' }} id="motherName" label={`${t('_Mother')}`}
                  {...register('motherName')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(33% - 8px)' }} id="tutorName" label={`${t('_Tutor')}`}
                  {...register('tutorName')} inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }} />
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'>
              {(getValues().status === '10' && getValues().unpaidAmount > 0) &&
                <Box sx={{ mt: 0.25, width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Link href="#" onClick={handleGoToTellerOperation}
                    sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                    {`${t('This student has unpaid amount')} : ${t('You can pay by clicking here')} ... `}
                  </Link>
                </Box>}
              {((getValues().id || 0) > 0) && <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1} sx={{ mt: 1, width: '100%' }} flexDirection='row' >
                <WidgetSummaryBasic title={`- ${t('Amount')}`} paletteColor={theme.palette['info']}
                  iconName='IoSendSharp' total={getValues().amount} />
                <WidgetSummaryBasic title={`- ${t('Amount paid')}`} paletteColor={theme.palette['success']}
                  iconName='IoSendSharp' total={getValues().amountPaid} />
                <WidgetSummaryBasic title={`- ${t('Due amount')}`} paletteColor={theme.palette['warning']}
                  iconName='IoSendSharp' total={getValues().dueAmount} />
                <WidgetSummaryBasic title={`- ${t('Unpaid amount')}`} paletteColor={theme.palette['error']}
                  iconName='IoSendSharp' total={getValues().unpaidAmount} />
              </Box>}
              <Box sx={{ mt: 1, width: '100%' }} >
                {generateIntialRegistrationLoading && <Box sx={{ mt: 1, width: '100%' }}>
                  <ThreeDots
                    color='#00BFFF'
                    height={'100%'}
                  //width={100}
                  //timeout={3000} //3 secs
                  />
                </Box>
                }
                <ArrayFieldTableEx<IRegistration, IBilling, 'id'>
                  mainObject={getValues()} fieldKey='id'
                  headCells={headBillingCells} rowsPathName={`billings`}
                  title={t('Billings')} rowActionIcon={billingRowActionIcon}
                  //onRowSelected={handleQuerySelected}
                  onRowDoubleClick={handleBillingDoubleClick}
                  refAppend={refAppendBillings as MutableRefObject<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>}
                  refUpdate={refUpdateBilling as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYear>>) => void>}
                  refRemove={refRemoveBilling as MutableRefObject<(index: number) => void>}

                  //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                  //displayMore={undefined}
                  toolbarActions={
                    getValues().status !== '95' ? [] : []
                    // [ { toolTip: `${t('Add')}...`, onClickIcon: handleAddBillings ,icon: AddCircleIcon,  },  
                    //   { toolTip: `${t('Add')}...`, onClickIcon: handleAddArticleBillings ,icon: AddCircleIcon,  },
                    // ]
                  }
                />
                {openBillingTypeFilter && <FormDialog open={openBillingTypeFilter} maxWidth='md'
                  okText={t('OK')} cancelText={t('Cancel')} title={t('Billing type filter')} onCancel={() => { setOpenBillingTypeFilter(false); }}
                  onClose={() => { setOpenBillingTypeFilter(false); }} onOk={handleOkBasicTextFilterBillingTypeForm}  >
                  <BasicTextFilterForm<IBillingType> {...basicFilterBillingType} />
                </FormDialog>}
                {openArticleBillingTypeFilter && <FormDialog open={openArticleBillingTypeFilter} maxWidth='md'
                  okText={t('OK')} cancelText={t('Cancel')} title={t('Article filter')} onCancel={() => { setOpenArticleBillingTypeFilter(false); }}
                  onClose={() => { setOpenArticleBillingTypeFilter(false); }} onOk={handleOkBasicTextFilterArticleBillingTypeForm}  >
                  <BasicTextFilterForm<IBillingType> {...basicFilterArticleBillingType} />
                </FormDialog>}
                {openTransportBillingTypeFilter && <FormDialog open={openTransportBillingTypeFilter} maxWidth='md'
                  okText={t('OK')} cancelText={t('Cancel')} title={t('Transport filter')} onCancel={() => { setOpenTransportBillingTypeFilter(false); }}
                  onClose={() => { setOpenTransportBillingTypeFilter(false); }} onOk={handleOkBasicTextFilterTransportBillingTypeForm}  >
                  <BasicTextFilterForm<IBillingType> {...basicFilterTransportBillingType} />
                </FormDialog>}

                {openBillingOption && <FormDialog open={openBillingOption} maxWidth='xs' height='35vh'
                  okText='' cancelText='' title={`${t('Billing')} - ${t('Option')}`} onCancel={() => { }}
                  onClose={() => { setOpenBillingOption(false); }} onOk={() => { setOpenBillingOption(false); }}  >
                  <Stack flexDirection='column' sx={{ pt: 0.25, pb: 0.25 }}>
                    <Box sx={{ mt: 1, width: '100%' }} key={` ${getValues().billings[billingIndex].deliveryArticleId} `}>
                      <Controller
                        key={`key-${getValues().billings[billingIndex].deliveryArticleId} `}
                        name={`billings.${billingIndex}.articleOptionName`}
                        control={control}
                        render={({ field }) => <TextField
                          {...field} sx={{ width: `calc(100% - 8px)` }}
                          inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }}
                          InputProps={{
                            endAdornment:
                              <InputAdornment position="end">
                                <IconButton color="primary" onClick={handleArticleAvailableOption}>
                                  <ArrowDropDownCircleIcon />
                                </IconButton>
                              </InputAdornment>
                          }}
                        />}
                      />
                      <Controller
                        key={`key-${getValues().billings[billingIndex].deliveryDescription} delivery`}
                        name={`billings.${billingIndex}.deliveryDescription`}

                        control={control}
                        render={({ field }) => <TextField
                          {...field} sx={{ width: `calc(100% - 8px)` }}
                          inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }}
                          multiline rows={2}
                        />}
                      />
                    </Box>
                  </Stack>
                </FormDialog>}
                {openArticleAvailable && <FormDialog open={openArticleAvailable} maxWidth='md'
                  okText='' cancelText='' title={`${t('Billing')} - ${t('Extension')}`} onCancel={() => { }}
                  onClose={() => { setOpenArticleAvailable(false); }} onOk={() => { setOpenArticleAvailable(false); }}  >
                  <Stack flexDirection='column' sx={{ pt: 0.25, pb: 0.25 }}>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <EnhancedTable<IBillingType> rows={optionAvailableArticles}
                        headCells={[
                          { id: 'id', label: t('Id'), display: true, type: 'numeric', },
                          //{id:'code', label : t('Code'),  display: true, type: 'string', },
                          { id: 'name', label: t('Name'), display: true, type: 'string', },

                          { id: 'articleOptionName', label: t('Option'), display: true, type: 'string', },
                          { id: 'availableQuantity', label: t('Quantity'), display: true, type: 'numeric', },

                          //{id:'description', label : t('Description'),  display: true, type: 'string', },
                          { id: 'deliveryDescription', label: t('Description'), display: true, type: 'string', },

                        ]}
                        title={`${t('_Leave_')}(s)`} objKey='id'
                        rowCheckedMode='single'
                        onRowCheckedSelectChange={undefined} order='desc' orderBy='id'
                        onRowDoubleClick={handleOptionAvailableArticleDoubleClick}
                        rowActionIcon={undefined}
                        toolbarActions={[
                          // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                        ]}
                      />

                    </Box>
                  </Stack>
                </FormDialog>
                }

                {openBillingExtension && <FormDialog open={openBillingExtension} maxWidth='sm'
                  okText='' cancelText='' title={`${t('Billing')} - ${t('Extension')}`} onCancel={() => { }}
                  onClose={() => { setOpenBillingExtension(false); }} onOk={() => { setOpenBillingExtension(false); }}  >
                  <Stack flexDirection='column' sx={{ pt: 0.25, pb: 0.25 }}>
                    <Box sx={{ mt: 1, width: '100%' }}>
                      {currentBilling && <>
                        {currentBilling.billingExtensions.map((extension, index) => {
                          //getValues().policyRisks[0].policyRiskCoverages[0].policyRiskCoverageExtensions[0].value;
                          //console.log(policyRiskCoverageIndex);
                          return (
                            <Box sx={{ mt: 1, width: '100%' }} key={`key-${extension.id} - ${index}`} >
                              <ExtensionValueField
                                name={`billings.${billingIndex}.billingExtensions.${index}.value`}
                                label={extension.extensionDescription}
                                type={extension.type}
                                options={getAsOptions(extensionEnumerationItems, extension.enumerationCode)}
                                itemsPerRow={2}
                              />
                            </Box>)
                        })}
                      </>}
                    </Box>
                  </Stack>
                </FormDialog>}
              </Box>
              {((getValues().id || 0) > 0 && getValues().status !== '95' && getValues().registrationMaterials.length > 0) &&
                <Box sx={{ mt: 3, width: '100%' }} >
                  <ArrayFieldTableEx<IRegistration, IRegistrationMaterial, 'id'>
                    key={`Material -  ${'reportTable.name'}`}
                    mainObject={getValues()} fieldKey='id'
                    headCells={headRegistrationMaterialCells}
                    rowsPathName={`registrationMaterials`}
                    title={`${t('School material')}`} rowActionIcon={registrationMaterialRowActionIcon}

                    //onRowSelected={handleRoleEntitySelected}                                                    
                    refAppend={refAppendRegistrationMaterials as MutableRefObject<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>}
                    refUpdate={refUpdateRegistrationMaterial as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYear>>) => void>}
                    refRemove={refRemoveRegistrationMaterial as MutableRefObject<(index: number) => void>}
                    //stateSelected={[selectedRequestDataItems, setSelectedRequestDataItems]}                                
                    toolbarActions={[
                      //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddRegistrationMaterials ,icon: AddCircleIcon,  },
                    ]}
                  />
                </Box>}
              {((getValues().id || 0) > 0 && getValues().status !== '95') && <Box sx={{ mt: 3, width: '100%' }} >
                <EnhancedTable<ITellerOperation>
                  rows={getValues().tellerOperations}
                  headCells={[
                    //{id:'id', label : t('Id'),  display: true, type: 'numeric', },
                    { id: 'reference', label: t('Reference'), display: true, type: 'string' },

                    { id: 'amount', label: t('Amount'), display: true, type: 'numeric' },
                    { id: 'operationDate', label: t('Operation date'), display: true, type: 'date' },
                    { id: 'issueDate', label: t('Issue date'), display: true, type: 'date' },
                    { id: 'isCancelled', label: t('Cancelled ?'), display: true, type: 'boolean' },
                  ]}
                  title={t(`List of payments`)} objKey='operationDate'
                  stateSelected={undefined}
                  onRowSelected={undefined} rowCheckedMode='single'
                  onRowCheckedSelectChange={undefined} order='desc' orderBy='operationDate'

                  onRowDoubleClick={handleTellerOperationDoubleClick}
                  rowActionIcon={tellerOperationRowActionIcon}
                  toolbarActions={[
                    // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                  ]}
                />
              </Box>}
              {openNewArticleBilling && <FormDialog open={openNewArticleBilling} maxWidth='md'
                okText={t('OK')} cancelText={t('Cancel')} title={`${t('Article')} ...`}
                okButtonDisabled={okButtonDisabledNewArticle}
                onCancel={() => { setOpenNewArticleBilling(false); }}
                onClose={() => { setOpenNewArticleBilling(false); }} onOk={handleOkCreateNewArticleBillingsRegistration}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <ArrayFieldTableEx<IRegistration, IBilling, 'id'>
                      mainObject={getValues()} fieldKey='id'
                      headCells={headNewArticleBillingCells} rowsPathName={`newArticleBillings`}
                      title={t('New billings')} rowActionIcon={newArticleBillingRowActionIcon}
                      //onRowSelected={handleQuerySelected}

                      refAppend={refAppendNewArticleBillings as MutableRefObject<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>}
                      refUpdate={refUpdateNewArticleBilling as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYear>>) => void>}
                      refRemove={refRemoveNewArticleBilling as MutableRefObject<(index: number) => void>}

                      //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                      //displayMore={undefined}
                      toolbarActions={getValues().status !== '10' ? [] : [
                        //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddNewBillings ,icon: AddCircleIcon,  },
                        { toolTip: `${t('Add')}...`, onClickIcon: handleAddNewArticleBillings, icon: AddCircleIcon, },

                      ]}
                    />
                  </Box>
                </Stack>
              </FormDialog>}
              {openNewTransportBilling && <FormDialog open={openNewTransportBilling} maxWidth='md'
                okText={t('OK')} cancelText={t('Cancel')} title={`${t('Transport')} ...`}
                okButtonDisabled={okButtonDisabledNewTransport}
                onCancel={() => { setOpenNewTransportBilling(false); }}
                onClose={() => { setOpenNewTransportBilling(false); }} onOk={handleOkCreateNewTransportBillingsRegistration}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }} key={`trans : ${(getValues().newTransportBillings || []).length}`}>
                    <TextField sx={{ width: 'calc(50% - 8px)' }} id="busDestinationName" label={`${t('Destination')}`}
                      {...register('busDestinationName')} inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton color="primary" onClick={handleClickOpenBusDestination} 
                                  disabled={(getValues().newTransportBillings || []).length > 0}>
                              <ArrowDropDownCircleIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }} />
                    <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                      label={t('Arrival ?')} key={`arr ${(getValues().newTransportBillings || []).length}`}
                      control={
                        <Controller
                          name='isArrival'
                          control={control}
                          render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange}

                            inputProps={{ readOnly: (getValues().newTransportBillings || []).length > 0 }} />}
                        />} />
                    <FormControlLabel sx={{ width: 'calc(25% - 8px)' }}
                      label={t('Departure ?')} key={`dep ${(getValues().newTransportBillings || []).length}`}
                      control={
                        <Controller
                          name='isDeparture'
                          control={control}
                          render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange}

                            inputProps={{ readOnly: (getValues().newTransportBillings || []).length > 0 }} />}
                        />} />

                  </Box>
                  {openBusDestinationFilter && <FormDialog open={openBusDestinationFilter} maxWidth='md'
                    okText='' cancelText='' title={t('Bus destination')} onCancel={() => { }}
                    onClose={() => { setOpenBusDestinationFilter(false); }} onOk={() => { setOpenBusDestinationFilter(false); }}  >
                    <BasicTextFilterForm<IBusDestination> {...basicFilterBusDestination} />
                  </FormDialog>}
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <ArrayFieldTableEx<IRegistration, IBilling, 'id'>
                      mainObject={getValues()} fieldKey='id'
                      headCells={headNewTransportBillingCells} rowsPathName={`newTransportBillings`}
                      title={t('New billings')} rowActionIcon={newTransportBillingRowActionIcon}
                      //onRowSelected={handleQuerySelected}

                      refAppend={refAppendNewTransportBillings as MutableRefObject<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>}
                      refUpdate={refUpdateNewTransportBilling as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYear>>) => void>}
                      refRemove={refRemoveNewTransportBilling as MutableRefObject<(index: number) => void>}

                      //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                      //displayMore={undefined}
                      toolbarActions={getValues().status !== '10' ? [] : [
                        //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddNewBillings ,icon: AddCircleIcon,  },
                        { toolTip: `${t('Add')}...`, onClickIcon: handleAddNewTransportBillings, icon: AddCircleIcon, },

                      ]}
                    />
                  </Box>
                </Stack>
              </FormDialog>}
              {openNewBilling && <FormDialog open={openNewBilling} maxWidth='sm'
                okText={t('OK')} cancelText={t('Cancel')} title={`${t('Billing')} ...`}
                okButtonDisabled={okButtonDisabledNewBilling}
                onCancel={() => { setOpenNewBilling(false); }}
                onClose={() => { setOpenNewBilling(false); }} onOk={handleOkCreateNewBillingsRegistration}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <ArrayFieldTableEx<IRegistration, IBilling, 'id'>
                      mainObject={getValues()} fieldKey='id'
                      headCells={headNewBillingCells} rowsPathName={`newBillings`}
                      title={t('New billings')} rowActionIcon={newBillingRowActionIcon}
                      //onRowSelected={handleQuerySelected}

                      refAppend={refAppendNewBillings as MutableRefObject<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>}
                      refUpdate={refUpdateNewBilling as MutableRefObject<(index: number, value: Partial<FieldArray<ISchoolYear>>) => void>}
                      refRemove={refRemoveNewBilling as MutableRefObject<(index: number) => void>}

                      //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                      //displayMore={undefined}
                      toolbarActions={getValues().status !== '10' ? [] : [
                        //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddNewBillings ,icon: AddCircleIcon,  },
                        { toolTip: `${t('Add')}...`, onClickIcon: handleAddNewBillings, icon: AddCircleIcon, },

                      ]}
                    />
                  </Box>
                </Stack>
              </FormDialog>}
              {openChangeClass && <FormDialog open={openChangeClass} maxWidth='sm' height='35vh'
                okText={` ${t('Save')}`} cancelText={t('Cancel')} title={`${t('Class')} ... `} onCancel={() => { }}
                onClose={() => { setOpenChangeClass(false); }} onOk={handleOkChangeClassAction}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Controller name='schoolYearClassId' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(100% - 8px)' }} id="schoolYearClassId"
                          label={t('Class')} inputProps={{ readOnly: false }} focused
                          helperText={isFalsy(schoolYearClassCountStudents) ? '' : `# ${t('Student')}(s) : ${schoolYearClassCountStudents} `}
                        >
                          {classes && classes.filter(x => x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId).map(
                            (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                </Stack>
              </FormDialog>
              }

              {openResetClass && <FormDialog open={openResetClass} maxWidth='sm' height='35vh'
                okText={` ${t('Save')}`} cancelText={t('Cancel')} title={`${t('Class')} ... `} onCancel={() => { }}
                onClose={() => { setOpenResetClass(false); }} onOk={handleOkResetClassAction}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Controller name='schoolYearClassId' control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(100% - 8px)' }} id="schoolYearClassId"
                          label={t('Class')} inputProps={{ readOnly: false }} focused
                          helperText={isFalsy(schoolYearClassCountStudents) ? '' : `# ${t('Student')}(s) : ${schoolYearClassCountStudents} `}
                        >
                          {classes && classes.map(
                            (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                </Stack>
              </FormDialog>
              }

              {displayEnumerationItemDialog && <DialogEnumerationItemForm
                {...{
                  open: displayEnumerationItemDialog,
                  title: '',
                  enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
                  afterSave: async () => { await refetchEnumerationItems() }
                }}
              />
              }
            </Stack>
          </Grid>

        </Grid>
      </Box>
    </FormProvider>
  )
}

